import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, getEnrichmentReviewEntityTypesList } from "../services/api";
import {
  augmentRelMissingEndpoint,
  wireRelEntityId,
} from "../utils/enrichmentMissingEndpoint";
import { normalizeEnrichmentRelationshipForPersist } from "../utils/enrichmentRelationshipPersist";
import {
  applyEntityTypeOverrideSideEffects,
  applyInstantMetamodelForRelationship,
  applyMetamodelReconcileResultsToPreview,
  buildEnrichmentEntityRoster,
  effectiveCatalogSurfaceType,
  effectiveEndpointTypesForValidation,
  isEnrichmentAnchorEntity,
  reconcileProposedRelationshipsForMetamodel,
  missingEndpointEntityTypeForCreate,
  normalizeTypeForCompare,
  parseRosterOverrideKey,
  proposedIndicesTouchingRosterKey,
  resolutionKey,
  rosterTypeOverrideKeyForGap,
} from "../utils/enrichmentEntityRoster";
import {
  anchorSummaryLine,
  applyCatalogEndpointCandidates,
  applyRelationshipDisplayOverride,
  alignInterfaceRowWithDataFlow,
  buildDecidedReviewItems,
  buildReviewQueue,
  clearCatalogEntityOutcome,
  clearDescriptionAppliedOutcome,
  clearProposedRelationshipOutcome,
  countQueueStats,
  cascadeDiscardAfterNameMatchSkip,
  filterAcceptableAiRecommendationItems,
  HIDDEN_REVIEW_REL_KINDS,
  hydrateReviewActionsFromPreview,
  interfaceStubEndpointsAreApplications,
  isDescriptionReviewResolved,
  isNameMatchReviewRow,
  listAutoCompletableCatalogStubs,
  hasUndecidedNameMatches,
  previewHasInFlightReviewSaves,
  countInFlightReviewSaves,
  shouldAutoCompleteEnrichmentReview,
  normalizeDescriptionText,
  pruneDeadReviewCards,
  refreshSolutionStructureCatalogRows,
  renameReviewItemEntity,
  reviewItemEditableEntities,
  setDescriptionAppliedOutcome,
  setDescriptionDiscardedOutcome,
  setReviewCompleteOutcome,
  stripStaleInProgressReviewOutcomes,
  wireCatalogPeerFromEntityMatch,
} from "../utils/enrichmentReviewQueue";
import {
  cancelEnrichmentReviewPersist,
  persistEnrichmentReviewNow,
  scheduleEnrichmentReviewPersist,
} from "../utils/debouncedEnrichmentReviewPersist";
import { emitGuideEvent } from "../v2/guide/guideEvents";
import EnrichmentReviewQueuePanel, {
  EntityTypeChangeConfirmDialog,
} from "./EnrichmentReviewQueuePanel";
import EnrichmentEntityPickerModal from "./EnrichmentEntityPickerModal";
import EnrichmentContextNotes from "./EnrichmentContextNotes";
import { extractionRationale } from "../utils/enrichmentRationale";
import {
  entityCatalogDescription,
  relationshipAssociationDescription,
} from "../utils/enrichmentEntityDescriptions";
import {
  appendEnrichmentReviewAudit,
  mergeJobAuditsIntoPreview,
  relationshipAuditFields,
  shouldRecordEnrichmentAuditEvent,
} from "../utils/enrichmentReviewAudit";
import {
  enrichmentReviewSaveStatusMessage,
  ENRICHMENT_DESCRIPTION_SAVE_HINT,
} from "../utils/enrichmentReviewSaveStatus";
import { buildEvidenceAcceptPayload, evidenceQuoteFromRow } from "../utils/enrichmentEvidenceContext";
import {
  applyReviewBackgroundPatch,
  attachReviewBackground,
  beginReviewBackgroundSave,
  detachReviewBackground,
  endReviewBackgroundSave,
  getReviewBackgroundInFlight,
  isReviewBackgroundDetached,
  subscribeReviewBackground,
  syncReviewBackgroundPreview,
  trackReviewBackgroundRagIds,
} from "../utils/enrichmentReviewBackground";

export { extractionRationale } from "../utils/enrichmentRationale";

export {
  augmentRelMissingEndpoint,
  wireRelEntityId,
} from "../utils/enrichmentMissingEndpoint";

export function parseJobResultDocument(job) {
  const raw = job?.result;
  if (raw == null) return null;
  let res;
  if (typeof raw === "string") {
    try {
      res = JSON.parse(raw);
    } catch {
      return null;
    }
  } else {
    res = raw;
  }
  if (!res || typeof res !== "object") return null;
  return res;
}

export function parseJobResult(job) {
  const doc = parseJobResultDocument(job);
  if (!doc) return null;
  const preview = doc.preview || null;
  if (!preview) return null;
  const merged = mergeJobAuditsIntoPreview(preview, doc);
  if (doc.evidence && typeof doc.evidence === "object" && !merged.evidence) {
    merged.evidence = doc.evidence;
  }
  return merged;
}

let cachedEntityTypeOptions = null;
let entityTypeOptionsPromise = null;

function loadEntityTypeOptionsOnce() {
  if (cachedEntityTypeOptions) {
    return Promise.resolve(cachedEntityTypeOptions);
  }
  if (!entityTypeOptionsPromise) {
    entityTypeOptionsPromise = getEnrichmentReviewEntityTypesList()
      .then((list) => {
        cachedEntityTypeOptions = Array.isArray(list) ? list : [];
        return cachedEntityTypeOptions;
      })
      .catch((e) => {
        entityTypeOptionsPromise = null;
        throw e;
      });
  }
  return entityTypeOptionsPromise;
}

/** Human-readable anchor provenance for enrichment review (catalog pick vs new Proposed row). */
export function buildAnchorProvenance(preview, editingEntity) {
  if (!preview || typeof preview !== "object") {
    return {
      variant: "unknown",
      title: "",
      lines: [],
      displayName: "",
      idLine: null,
      show: false,
    };
  }
  const method = String(preview.anchor_resolution_method || "").toLowerCase();
  const isNew = preview.anchor_is_new_provisional === true;
  const eid = String(preview.anchor_application_id || editingEntity?.id || "").trim();
  const displayName =
    String(editingEntity?.name || preview.anchor_name || "").trim() || "Application";
  const matchedAt = preview.anchor_matched_name_at_queue;
  const approval = preview.anchor_entity_approval_status;

  let variant = "catalog";
  let title = "App for this upload";
  const lines = [];

  if (method === "auto_created" || isNew) {
    variant = "provisional";
    title = "New app (we created it)";
    lines.push(
      "We created a new app on your map for this upload. The name may have been guessed by AI and might not appear word-for-word in your document."
    );
    lines.push(
      "If this should link to an app you already have, rename or merge it before approving connections."
    );
  } else if (method === "llm") {
    title = "App from your map (AI picked)";
    lines.push(
      "We matched this upload to an app already on your map. Confirm it is the system you meant."
    );
  } else if (method === "heuristic") {
    title = "App from your map (name in your upload)";
    lines.push(
      "This app matches a name or alias that appears in your upload."
    );
  } else if (method === "existing") {
    title = "App from your map (exact name match)";
    lines.push(
      "This app on your map has the same name we found in your upload."
    );
  } else if (method === "explicit") {
    variant = "explicit";
    title = "App you chose when uploading";
    lines.push("You picked this app when you added the knowledge.");
  } else {
    variant = "unknown";
    title = "App for this upload";
    lines.push(
      "We could not tell how this app was chosen. Check the name matches what you expect."
    );
  }

  if (matchedAt && String(matchedAt).trim() && String(matchedAt).trim() !== displayName) {
    lines.push(`Name matched when queued: ${String(matchedAt).trim()}`);
  }
  if (
    approval &&
    String(approval).toLowerCase() === "proposed" &&
    method !== "auto_created" &&
    !isNew
  ) {
    lines.push(
      `This app is still ${approval} on your map—confirm it before treating suggestions as final.`
    );
  }

  const idLine = eid ? `Entity ID: ${eid}` : null;

  return {
    variant,
    title,
    lines,
    displayName,
    idLine,
    show: true,
  };
}

function buildRationale(rel, anchorName) {
  return relationshipAssociationDescription(rel, { anchorName });
}

function relationshipLabel(rel) {
  const raw = String(rel?.relationship_type || rel?.kind || "").trim();
  const map = {
    provider_to_consumer: "sends data to",
    consumer_from_provider: "receives data from",
    provides: "provides interface",
    consumes: "consumes interface",
    "organization uses application": "is used by",
    "application supports business-capability": "supports business capability",
    "application supports business-context": "supports business context",
  };
  return map[raw] || raw || "relationship";
}

/** Anchor display for org↔app edges: clarify anchor type when the name omits it. */
function anchorAsTypedPhrase(anchorName, anchorEntityType) {
  const type = String(anchorEntityType || "application")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
  const typeWord = type === "application" ? "application" : type.replace(/-/g, " ");
  const a = String(anchorName || "").trim() || `This ${typeWord}`;
  if (new RegExp(`\\b${typeWord.replace(/\s+/g, "\\s+")}\\b`, "i").test(a)) return a;
  if (type === "application") return `${a} application`;
  return a;
}

function relationshipSentence(anchorName, rel, anchorEntityType = "application") {
  const typeWord =
    String(anchorEntityType || "application")
      .trim()
      .toLowerCase()
      .replace(/_/g, "-")
      .replace(/-/g, " ") || "entity";
  const anchor = anchorName || `This ${typeWord}`;
  const peer = peerLabel(rel);
  if (isObsoleteRel(rel)) {
    return `Remove ${peer} from ${anchor}? Knowledge no longer supports this connection.`;
  }
  const rt = String(rel?.relationship_type || "").trim();
  const map = {
    "application uses technology-component": `${anchor} uses technology component ${peer}.`,
    "application accesses data-object": `${anchor} accesses data object ${peer}.`,
    "application runs on platform": `${anchor} runs on platform ${peer}.`,
    "initiative affects application": `There is a new initiative ${peer} related to ${anchor}.`,
    "organization uses application": `${anchorAsTypedPhrase(
      anchorName || anchor,
      "application"
    )} is used by ${peer}.`,
  };
  if (map[rt]) return map[rt];
  return `As per the text, ${anchor} ${relationshipLabel(rel)} ${peer}.`;
}

function peerLabel(rel) {
  return (
    rel.peer_name ||
    rel.surface_target_name ||
    rel.surface_source_name ||
    rel.surface_other_application ||
    rel.surface_child_application ||
    rel.target_entity_type ||
    rel.target_entity_id ||
    rel.source_entity_id ||
    "—"
  );
}

function isResolvable(rel) {
  if (isObsoleteRel(rel)) {
    return Boolean(String(rel?.relationship_id || "").trim());
  }
  const sid = String(rel?.source_entity_id || "").trim();
  const tid = String(rel?.target_entity_id || "").trim();
  return Boolean(sid && tid);
}

function isObsoleteRel(rel) {
  const status = String(rel?.status || rel?.diff_status || "").trim().toLowerCase();
  const kind = String(rel?.kind || "").trim().toLowerCase();
  return status === "obsolete" || kind === "obsolete";
}

function normName(s) {
  return String(s || "")
    .trim()
    .toLowerCase();
}

/** Human-readable entity name for roster type-change UI (never show raw override keys). */
function displayNameForRosterTypeChange(entry, preview) {
  const direct = String(
    entry?.label || entry?.name || entry?.surfaceName || ""
  ).trim();
  if (direct && !direct.includes("|") && !direct.startsWith("resolution:") && !direct.startsWith("missing:")) {
    return direct;
  }
  const parsed = parseRosterOverrideKey(entry?.key);
  if (parsed?.surfaceNorm) {
    const rows = Array.isArray(preview?.resolution) ? preview.resolution : [];
    const match = rows.find(
      (row) => normName(row?.surface_name) === parsed.surfaceNorm
    );
    if (match?.surface_name) return String(match.surface_name).trim();
    return parsed.surfaceNorm
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }
  return "this entity";
}

function isInterfaceKind(kind) {
  return kind === "interface_flow" || kind === "interface_link";
}

function isDuplicateRelationshipError(err) {
  const msg = String(err?.message || "").toLowerCase();
  return (
    msg.includes("already exists") ||
    msg.includes("duplicate") ||
    msg.includes("unique constraint")
  );
}

function extractActionErrorMessage(err, fallback) {
  const detail =
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.response?.data?.error;
  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const nested = detail.message || detail.detail || detail.error;
    if (typeof nested === "string" && nested.trim()) return nested.trim();
    const end = detail.end === "source" ? "source" : detail.end === "target" ? "target" : "";
    const eid = String(detail.entity_id || "").trim();
    if (end && eid) {
      return `${end === "source" ? "Source" : "Target"} system not found (${eid}). Match the name first or add it as new before approving.`;
    }
  }
  if (Array.isArray(detail)) {
    const merged = detail
      .map((x) => (typeof x === "string" ? x : x?.msg || ""))
      .filter(Boolean)
      .join(" ");
    if (merged.trim()) return merged.trim();
  }
  const msg = typeof err?.message === "string" ? err.message.trim() : "";
  return msg || fallback;
}

function splitAliases(raw) {
  return String(raw || "")
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function mergeAliasNames(existingAliases, addAlias) {
  const existing = splitAliases(existingAliases);
  const add = String(addAlias || "").trim();
  if (!add) return existing.join(", ");
  const has = existing.some((x) => x.toLowerCase() === add.toLowerCase());
  if (has) return existing.join(", ");
  return [...existing, add].join(", ");
}

function findResolutionRow(preview, surfaceName, entityType) {
  const missName = normName(surfaceName);
  const missType = normName(entityType);
  const rows = Array.isArray(preview?.resolution) ? preview.resolution : [];
  const overrides = preview?.enrichment_review_outcomes?.entity_type_overrides || {};

  const exact =
    rows.find(
      (row) =>
        normName(row?.surface_name) === missName &&
        normName(row?.entity_type) === missType
    ) || null;
  if (exact) return exact;

  const overrideMatch =
    rows.find((row) => {
      if (normName(row?.surface_name) !== missName) return false;
      const rosterKey = `resolution:${resolutionKey(row)}`;
      const effectiveType = normName(overrides[rosterKey] ?? row?.entity_type);
      return effectiveType === missType;
    }) || null;
  if (overrideMatch) return overrideMatch;

  const bySurface = rows.filter(
    (row) => normName(row?.surface_name) === missName && row?.kind !== "linked_existing"
  );
  if (bySurface.length === 1) return bySurface[0];
  return null;
}

function patchPreviewAfterExistingMatch(prev, opts) {
  const surfaceName = String(opts?.surfaceName || "").trim();
  const entityType = String(opts?.entityType || "").trim();
  const entityId = String(opts?.entityId || "").trim();
  if (!prev || !surfaceName || !entityType || !entityId) return prev;
  const missName = normName(surfaceName);
  const missType = normName(entityType);
  const next = { ...prev };

  let foundResolution = false;
  next.resolution = (prev.resolution || []).map((r) => {
    if (normName(r?.surface_name) !== missName) return r;
    if (normName(r?.entity_type) !== missType) return r;
    foundResolution = true;
    return {
      ...r,
      kind: "linked_existing",
      entity_id: entityId,
      reason_code: "user_confirmed_similarity_match",
    };
  });
  if (!foundResolution) {
    next.resolution = [
      ...(next.resolution || []),
      {
        surface_name: surfaceName,
        entity_type: entityType,
        kind: "linked_existing",
        entity_id: entityId,
        reason_code: "user_confirmed_similarity_match",
        candidate_matches: [],
      },
    ];
  }

  next.proposed_relationships = (prev.proposed_relationships || []).map((rel) =>
    wireRelEntityId(rel, entityId, surfaceName, entityType)
  );

  next.proposed_catalog_entities = (prev.proposed_catalog_entities || []).map((row) => {
    const out = { ...row };
    if (
      missType === "application" &&
      normName(row?.provider_application_name) === missName &&
      !String(row?.provider_entity_id || "").trim()
    ) {
      out.provider_entity_id = entityId;
      out.provider_candidate_matches = [];
    }
    if (
      missType === "application" &&
      normName(row?.consumer_application_name) === missName &&
      !String(row?.consumer_entity_id || "").trim()
    ) {
      out.consumer_entity_id = entityId;
      out.consumer_candidate_matches = [];
    }
    return wireCatalogPeerFromEntityMatch(out, surfaceName, entityId, entityType);
  });

  return refreshSolutionStructureCatalogRows(next);
}

function patchPreviewAfterDiscardPotentialMatch(prev, opts) {
  const surfaceName = String(opts?.surfaceName || "").trim();
  const entityType = String(opts?.entityType || "").trim();
  if (!prev || !surfaceName || !entityType) return prev;
  const missName = normName(surfaceName);
  const missType = normName(entityType);
  let next = { ...prev };

  next.resolution = (prev.resolution || []).map((r) => {
    if (normName(r?.surface_name) !== missName) return r;
    if (normName(r?.entity_type) !== missType) return r;
    return {
      ...r,
      kind: "discarded",
      reason_code: "user_discarded_potential_match",
    };
  });

  // Keep rows in-place for traceability; discard state is rendered from action maps.
  next.proposed_relationships = prev.proposed_relationships || [];
  next.proposed_catalog_entities = prev.proposed_catalog_entities || [];

  next = cascadeDiscardAfterNameMatchSkip(next, surfaceName, entityType);
  return pruneDeadReviewCards(next);
}

/** User declined catalog similarity suggestions — treat surface name as a new entity going forward. */
function patchPreviewAfterKeepNew(prev, opts) {
  const surfaceName = String(opts?.surfaceName || "").trim();
  const entityType = String(opts?.entityType || "").trim();
  if (!prev || !surfaceName || !entityType) return prev;
  const missName = normName(surfaceName);
  const missType = normName(entityType);
  const next = { ...prev };

  let found = false;
  next.resolution = (prev.resolution || []).map((r) => {
    if (normName(r?.surface_name) !== missName) return r;
    if (normName(r?.entity_type) !== missType) return r;
    found = true;
    return {
      ...r,
      kind: "create_new",
      reason_code: "user_kept_as_new",
      // Keep candidate_matches so the review row stays visible with outcome text until reload;
      // enrichment_review_outcomes.resolution_matches records the decision for persistence.
    };
  });
  if (!found) {
    next.resolution = [
      ...(next.resolution || []),
      {
        surface_name: surfaceName,
        entity_type: entityType,
        kind: "create_new",
        reason_code: "user_kept_as_new",
        candidate_matches: [],
      },
    ];
  }

  return next;
}

/**
 * After creating a catalog entity, wire new IDs into the in-memory preview so Approve can enable.
 */
export function patchPreviewAfterCatalogCreate(prev, created, opts) {
  const id = created?.id;
  const entityType = opts?.entityType || created?.type || "";
  if (!id || !prev) return prev;
  const stubKey = opts?.stubKey;
  const etLower = normName(entityType);

  const patchRel = (r) => {
    if (stubKey && r.interface_suggestion_key === stubKey) {
      if (r.missing_end === "target" && normName(r.missing_entity_type) === "interface") {
        return {
          ...r,
          target_entity_id: id,
          missing_end: undefined,
          missing_entity_type: undefined,
          missing_surface_name: undefined,
        };
      }
    }
    const wired = wireRelEntityId(r, id, created?.name, entityType);
    if (wired !== r) return wired;

    const miss = r.missing_surface_name;
    if (!miss || normName(r.missing_entity_type) !== etLower) return r;
    if (normName(miss) !== normName(created?.name)) return r;
    if (r.missing_end === "target") {
      return {
        ...r,
        target_entity_id: id,
        missing_end: undefined,
        missing_entity_type: undefined,
        missing_surface_name: undefined,
      };
    }
    if (r.missing_end === "source") {
      return {
        ...r,
        source_entity_id: id,
        missing_end: undefined,
        missing_entity_type: undefined,
        missing_surface_name: undefined,
      };
    }
    return r;
  };

  const patchCatalogRow = (row) => {
    let out = row;
    if (stubKey && row.interface_suggestion_key === stubKey) {
      out = { ...out, created_entity_id: id };
    }
    const createdName = normName(created?.name);
    if (etLower === "application" && createdName) {
      if (
        normName(row?.provider_application_name) === createdName &&
        !String(row?.provider_entity_id || "").trim()
      ) {
        out = {
          ...out,
          provider_entity_id: id,
          provider_candidate_matches: [],
        };
      }
      if (
        normName(row?.consumer_application_name) === createdName &&
        !String(row?.consumer_entity_id || "").trim()
      ) {
        out = {
          ...out,
          consumer_entity_id: id,
          consumer_candidate_matches: [],
        };
      }
    }
    if (stubKey && row.interface_suggestion_key === stubKey && etLower === "interface") {
      out = { ...out, existing_interface_id: id };
    }
    if (etLower === "technology-component" && createdName) {
      if (
        (normName(row?.technology_component_name) === createdName ||
          normName(row?.implementation_peer_name) === createdName) &&
        !String(row?.technology_component_entity_id || "").trim()
      ) {
        out = {
          ...out,
          technology_component_entity_id: id,
          technology_candidate_matches: [],
          implementation_peer_entity_id: id,
          implementation_peer_candidate_matches: [],
        };
      }
    }
    if (etLower === "platform" && createdName) {
      if (
        (normName(row?.platform_name) === createdName ||
          normName(row?.implementation_peer_name) === createdName) &&
        !String(row?.platform_entity_id || "").trim()
      ) {
        out = {
          ...out,
          platform_entity_id: id,
          platform_candidate_matches: [],
          implementation_peer_entity_id: id,
          implementation_peer_candidate_matches: [],
        };
      }
    }
    return out;
  };

  return refreshSolutionStructureCatalogRows({
    ...prev,
    proposed_relationships: (prev.proposed_relationships || []).map(patchRel),
    proposed_catalog_entities: (prev.proposed_catalog_entities || []).map(patchCatalogRow),
  });
}

function patchPreviewAfterRelationshipApprove(prev, rel, relationshipId) {
  if (!prev || !relationshipId || !rel) return prev;
  const rid = String(relationshipId).trim();
  if (!rid) return prev;
  const next = { ...prev };
  next.proposed_relationships = (prev.proposed_relationships || []).map((r) => {
    const sameType =
      normName(r?.relationship_type || r?.kind) ===
      normName(rel?.relationship_type || rel?.kind);
    const sameSource =
      String(r?.source_entity_id || "").trim() ===
      String(rel?.source_entity_id || "").trim();
    const sameTarget =
      String(r?.target_entity_id || "").trim() ===
      String(rel?.target_entity_id || "").trim();
    const sameSurface =
      normName(r?.surface_target_name || r?.surface_source_name) ===
      normName(rel?.surface_target_name || rel?.surface_source_name);
    const sameSuggestionKey =
      String(r?.interface_suggestion_key || "").trim() ===
      String(rel?.interface_suggestion_key || "").trim();
    if (
      sameType &&
      sameSource &&
      sameTarget &&
      (sameSurface || sameSuggestionKey)
    ) {
      return {
        ...r,
        existing_relationship_id: rid,
      };
    }
    return r;
  });
  return next;
}

/** Persisted per-row review outcomes (saved on the job result.preview). */
function setProposedRelationshipOutcome(prev, key, status) {
  if (!prev) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const pr = { ...(outcomes.proposed_relationships || {}) };
  pr[key] = status;
  return {
    ...prev,
    enrichment_review_outcomes: { ...outcomes, proposed_relationships: pr },
  };
}

function setCatalogEntityOutcome(prev, catalogKey, status) {
  if (!prev) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const ce = { ...(outcomes.catalog_entities || {}) };
  ce[catalogKey] = status;
  return {
    ...prev,
    enrichment_review_outcomes: { ...outcomes, catalog_entities: ce },
  };
}

async function mapPool(items, limit, fn) {
  const ret = [];
  let i = 0;
  async function worker() {
    while (i < items.length) {
      const idx = i++;
      ret[idx] = await fn(items[idx]);
    }
  }
  const n = Math.max(1, Math.min(limit || 1, items.length || 1));
  await Promise.all(Array.from({ length: n }, () => worker()));
  return ret;
}

function setResolutionMatchOutcome(prev, resolutionKey, status) {
  if (!prev) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const rm = { ...(outcomes.resolution_matches || {}) };
  rm[resolutionKey] = status;
  return {
    ...prev,
    enrichment_review_outcomes: { ...outcomes, resolution_matches: rm },
  };
}

function effectiveRelReviewState(preview, rel, idx, relActions) {
  const key = `p${idx}`;
  const live = relActions[key] || {};
  const po = preview?.enrichment_review_outcomes?.proposed_relationships?.[key];
  // Live terminal outcomes beat stale persisted in-flight flags (e.g. approving after save finished).
  if (live.state === "approved" || live.state === "discarded" || live.state === "error") {
    return {
      state: live.state,
      error: live.error || "",
      relationshipId: live.relationshipId,
    };
  }
  // Persisted preview outcomes must win over stale in-memory state (e.g. metamodel auto-discard).
  if (po === "approved" || po === "discarded") {
    return {
      state: po,
      error: "",
      relationshipId: live.relationshipId,
    };
  }
  if (po === "approving" || po === "discarding") {
    return {
      state: po,
      error: live.error || "",
      relationshipId: live.relationshipId,
    };
  }
  if (
    live.state === "approving" ||
    live.state === "error" ||
    live.state === "discarding"
  ) {
    return live;
  }
  if (live.state === "approved" || live.state === "discarded") {
    return live;
  }
  if (String(rel?.existing_relationship_id || "").trim()) {
    return { state: "approved", error: "", relationshipId: rel.existing_relationship_id };
  }
  return live;
}

function effectiveCatalogAction(preview, ck, catalogActions) {
  const live = catalogActions[ck] || {};
  if (live.state === "creating" || live.state === "error") return live;
  if (live.state) return live;
  const po = preview?.enrichment_review_outcomes?.catalog_entities?.[ck];
  if (po === "created") return { state: "created", error: "" };
  if (po === "discarded") return { state: "discarded", error: "" };
  if (po === "creating") return { state: "creating", error: live.error || "" };
  return live;
}

function effectiveMatchAction(preview, rk, matchActions) {
  const live = matchActions[rk] || {};
  if (live.state === "confirming" || live.state === "error") return live;
  if (live.state) return live;
  const po = preview?.enrichment_review_outcomes?.resolution_matches?.[rk];
  if (po === "confirmed" || po === "discarded" || po === "kept_new") {
    return { state: po, error: "" };
  }
  return live;
}

function effectiveRosterRowState(preview, rosterKey, rosterRowActions) {
  const live = rosterRowActions[rosterKey] || {};
  if (live.state === "approved" || live.state === "discarded") return live;
  const discarded = (preview?.enrichment_review_outcomes?.discarded_roster_keys || []).includes(
    rosterKey
  );
  if (discarded) return { state: "discarded", error: live.error || "" };
  const po = preview?.enrichment_review_outcomes?.entity_roster_approvals?.[rosterKey];
  if (po === "approved") return { state: "approved", error: "" };
  return live;
}

/** Stable key for proposed_catalog_entities / interface stub rows (matches UI state maps). */
export function catalogStubKey(row, idx) {
  return row?.interface_suggestion_key || `stub-${idx}`;
}

/** User finished approve/discard (or nothing to do) for one proposed relationship row. */
export function isProposedRelationshipRowComplete(preview, rel, idx, relActions) {
  if (String(rel?.existing_relationship_id || "").trim()) return true;
  if (HIDDEN_REVIEW_REL_KINDS.has(String(rel?.kind || "").trim())) return true;
  const eff = effectiveRelReviewState(preview, rel, idx, relActions);
  return eff?.state === "approved" || eff?.state === "discarded";
}

/** Interface stub row fully handled or already satisfied by catalog. */
export function isCatalogStubComplete(preview, row, idx, catalogActions) {
  const ck = catalogStubKey(row, idx);
  const cact = effectiveCatalogAction(preview, ck, catalogActions);
  const done = cact.state === "created" || Boolean(row?.created_entity_id);
  if (done) return true;
  return cact.state === "discarded";
}

function setEntityTypeOverridesOutcome(prev, patch) {
  if (!prev) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const merged = { ...(outcomes.entity_type_overrides || {}), ...patch };
  return {
    ...prev,
    enrichment_review_outcomes: {
      ...outcomes,
      entity_type_overrides: merged,
    },
  };
}

function setRelationshipMetamodelAdjustmentsOutcome(prev, patch) {
  if (!prev) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const merged = { ...(outcomes.relationship_metamodel_adjustments || {}), ...patch };
  return {
    ...prev,
    enrichment_review_outcomes: {
      ...outcomes,
      relationship_metamodel_adjustments: merged,
    },
  };
}

function patchProposedRelationshipAtIndex(prev, idx, row) {
  if (!prev || idx < 0) return prev;
  const rows = Array.isArray(prev.proposed_relationships)
    ? [...prev.proposed_relationships]
    : [];
  if (idx >= rows.length) return prev;
  rows[idx] = row;
  return { ...prev, proposed_relationships: rows };
}

function setDiscardedRosterKeysOutcome(prev, keys) {
  if (!prev) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const cur = new Set([...(outcomes.discarded_roster_keys || [])]);
  (keys || []).forEach((k) => cur.add(k));
  return {
    ...prev,
    enrichment_review_outcomes: {
      ...outcomes,
      discarded_roster_keys: Array.from(cur),
    },
  };
}

function setEntityRosterApprovalsOutcome(prev, patch) {
  if (!prev) return prev;
  const outcomes = { ...(prev.enrichment_review_outcomes || {}) };
  const merged = { ...(outcomes.entity_roster_approvals || {}), ...patch };
  return {
    ...prev,
    enrichment_review_outcomes: {
      ...outcomes,
      entity_roster_approvals: merged,
    },
  };
}

/**
 * Batch-create missing interface ends + interface + provides/consumes via mapWrites.
 * @returns {{ providerId: string, consumerId: string, interfaceId: string, createdEntities: any[], relationships: any[], affectedEntityIds: string[] }}
 */
async function applyMapWritesForInterface({
  missingEnds = [],
  interfaceSpec = null,
  providerId = "",
  consumerId = "",
  existingInterfaceId = "",
  rationale = "",
  deferRagRegeneration = true,
  evidence = null,
}) {
  const evidenceBlob =
    evidence && typeof evidence === "object" ? evidence : null;
  const entities = [];
  for (const end of missingEnds) {
    entities.push({
      client_key: end.client_key,
      name: end.name,
      type: end.type,
      approval_status: end.approval_status || "Proposed",
      description: end.description || "",
      confidence: end.confidence ?? 0.85,
      ...(evidenceBlob ? { evidence: evidenceBlob } : {}),
    });
  }

  let interfaceClientKey = null;
  const existingIface = String(existingInterfaceId || "").trim();
  if (!existingIface && interfaceSpec) {
    interfaceClientKey =
      String(interfaceSpec.client_key || "e_iface").trim() || "e_iface";
    entities.push({
      client_key: interfaceClientKey,
      name: interfaceSpec.name,
      type: interfaceSpec.type || "interface",
      approval_status: interfaceSpec.approval_status || "Proposed",
      description: interfaceSpec.description || "",
      confidence: interfaceSpec.confidence ?? 0.85,
      ...(evidenceBlob ? { evidence: evidenceBlob } : {}),
    });
  }

  const provId = String(providerId || "").trim();
  const consId = String(consumerId || "").trim();
  const providerRef = provId || "$e_prov";
  const consumerRef = consId || "$e_cons";
  const interfaceRef = existingIface || `$${interfaceClientKey || "e_iface"}`;

  const relationships = [
    {
      client_key: "r_provides",
      source_ref: providerRef,
      target_ref: interfaceRef,
      type: "provides",
      status: "Approved",
      description: rationale,
      confidence: 0.9,
      ...(evidenceBlob ? { evidence: evidenceBlob } : {}),
    },
    {
      client_key: "r_consumes",
      source_ref: consumerRef,
      target_ref: interfaceRef,
      type: "consumes",
      status: "Approved",
      description: rationale,
      confidence: 0.9,
      ...(evidenceBlob ? { evidence: evidenceBlob } : {}),
    },
  ];

  const result = await api.mapWrites({
    entities,
    relationships,
    deferRagRegeneration,
    evidence: evidenceBlob || undefined,
  });

  if (
    result &&
    result.success === false &&
    Array.isArray(result.errors) &&
    result.errors.length
  ) {
    const msg = result.errors
      .map((e) => e.error || e.message || "error")
      .filter(Boolean)
      .join("; ");
    throw new Error(msg || "mapWrites failed");
  }

  const byKey = {};
  for (const ent of result?.entities || []) {
    const ck = String(ent?.client_key || "").trim();
    if (ck) byKey[ck] = ent;
  }

  const resolvedProvider = provId || String(byKey.e_prov?.id || "").trim();
  const resolvedConsumer = consId || String(byKey.e_cons?.id || "").trim();
  const resolvedInterface =
    existingIface ||
    String(byKey[interfaceClientKey || "e_iface"]?.id || "").trim();

  if (!resolvedProvider || !resolvedConsumer || !resolvedInterface) {
    throw new Error(
      "mapWrites did not resolve provider, consumer, and interface ids"
    );
  }

  return {
    providerId: resolvedProvider,
    consumerId: resolvedConsumer,
    interfaceId: resolvedInterface,
    createdEntities: result?.entities || [],
    relationships: result?.relationships || [],
    affectedEntityIds: result?.affected_entity_ids || [],
  };
}

const EnrichReviewDialog = ({
  open,
  onClose,
  editingEntity,
  preview,
  reviewJobId = null,
  onRelationshipsApplied,
  onDescriptionApplied,
  onPreviewPatch,
  onReviewPersisted,
  onQueueStatsChange,
  onSaveBusyChange,
  inline = false,
  theme = "default",
  /** Opening Session: accept/discard records decisions only; publish writes the map. */
  deferMapWrites = false,
}) => {
  const applyPreviewPatch = useCallback(
    (mutator, auditEvent, options = {}) => {
      const persistImmediately = Boolean(options.persistImmediately);
      const jid = String(reviewJobId || "").trim();

      // After leave, React preview is gone — keep writing via the durable session.
      if (jid && isReviewBackgroundDetached(jid)) {
        const next = applyReviewBackgroundPatch(jid, mutator, auditEvent, {
          persistImmediately: true,
        });
        if (next) previewRef.current = next;
        return;
      }

      if (typeof onPreviewPatch !== "function") {
        if (jid) {
          const next = applyReviewBackgroundPatch(jid, mutator, auditEvent, {
            persistImmediately: true,
          });
          if (next) previewRef.current = next;
        }
        return;
      }

      onPreviewPatch((prev) => {
        let next = mutator(prev);
        const recordAudit =
          auditEvent && shouldRecordEnrichmentAuditEvent(auditEvent);
        if (recordAudit && next) {
          next = appendEnrichmentReviewAudit(next, auditEvent);
        }
        if (next) previewRef.current = next;
        if (jid && next) {
          syncReviewBackgroundPreview(jid, next);
          const auditForPersist = recordAudit ? auditEvent : null;
          if (persistImmediately) {
            void persistEnrichmentReviewNow(jid, next, auditForPersist)
              .then(() => {
                onReviewPersisted?.();
              })
              .catch((e) => {
                console.warn("Failed to persist enrichment review:", e);
              });
          } else {
            scheduleEnrichmentReviewPersist(jid, next, auditForPersist);
          }
        }
        return next;
      }, auditEvent);
    },
    [onPreviewPatch, reviewJobId, onReviewPersisted]
  );

  const [relActions, setRelActions] = useState({});
  const [catalogActions, setCatalogActions] = useState({});
  const [descBusy, setDescBusy] = useState(false);
  const [descAccepted, setDescAccepted] = useState(false);
  const [descError, setDescError] = useState("");
  const [descSkipped, setDescSkipped] = useState(false);
  const [matchActions, setMatchActions] = useState({});
  const [globalActionError, setGlobalActionError] = useState("");
  const [entityTypeOptions, setEntityTypeOptions] = useState([]);
  const [rosterRowActions, setRosterRowActions] = useState({});
  const [metamodelSyncNotice, setMetamodelSyncNotice] = useState(null);
  const [saveBusyTick, setSaveBusyTick] = useState(0);
  const [closingReview, setClosingReview] = useState(false);
  const [bulkAcceptProgress, setBulkAcceptProgress] = useState(null);
  const [entityPickerState, setEntityPickerState] = useState(null);
  const [typeChangeConfirmation, setTypeChangeConfirmation] = useState(null);
  const [typeChangePending, setTypeChangePending] = useState(null);

  const catalogSaveInFlightRef = useRef(0);
  const descBusyRef = useRef(false);
  const matchActionsRef = useRef({});

  const bumpSaveBusy = useCallback(() => {
    setSaveBusyTick((t) => t + 1);
  }, []);

  const beginCatalogSave = useCallback(() => {
    catalogSaveInFlightRef.current += 1;
    const jid = String(reviewJobId || "").trim();
    if (jid) beginReviewBackgroundSave(jid);
    bumpSaveBusy();
  }, [bumpSaveBusy, reviewJobId]);

  const endCatalogSave = useCallback(() => {
    catalogSaveInFlightRef.current = Math.max(0, catalogSaveInFlightRef.current - 1);
    const jid = String(reviewJobId || "").trim();
    if (jid) endReviewBackgroundSave(jid);
    bumpSaveBusy();
  }, [bumpSaveBusy, reviewJobId]);

  const setDescBusyTracked = useCallback((busy) => {
    descBusyRef.current = Boolean(busy);
    setDescBusy(descBusyRef.current);
    bumpSaveBusy();
  }, [bumpSaveBusy]);

  const deferredRagEntityIdsRef = useRef(new Set());
  const verifiedEntityIdsRef = useRef(new Set());
  const deferRag = { deferRagRegeneration: true };

  const trackDeferredRagEntities = useCallback((...ids) => {
    ids.forEach((id) => {
      const s = String(id || "").trim();
      if (s) deferredRagEntityIdsRef.current.add(s);
    });
    const jid = String(reviewJobId || "").trim();
    if (jid) trackReviewBackgroundRagIds(jid, ...ids);
  }, [reviewJobId]);

  const flushDeferredRagSummaries = useCallback(() => {
    const ids = [...deferredRagEntityIdsRef.current];
    if (!ids.length) return;
    deferredRagEntityIdsRef.current.clear();
    void api.regenerateRagSummariesBatch(ids, { background: true }).catch((e) => {
      console.warn("Deferred enrichment RAG refresh failed:", e);
    });
  }, []);

  const persistFlushDoneRef = useRef(false);

  const flushReviewPersist = useCallback(() => {
    const jid = String(reviewJobId || "").trim();
    const snap = previewRef.current;
    if (!jid || !snap || persistFlushDoneRef.current) return Promise.resolve();
    persistFlushDoneRef.current = true;
    cancelEnrichmentReviewPersist(jid);
    return persistEnrichmentReviewNow(jid, snap, null, { force: true })
      .then(() => onReviewPersisted?.())
      .catch((e) => {
        console.warn("Failed to persist enrichment review:", e);
        persistFlushDoneRef.current = false;
      });
  }, [onReviewPersisted, reviewJobId]);

  const waitForCatalogSavesIdle = useCallback(() => {
    return new Promise((resolve) => {
      const started = Date.now();
      const maxMs = 120000;
      const tick = () => {
        if (catalogSaveInFlightRef.current === 0 && !descBusyRef.current) {
          resolve();
          return;
        }
        if (Date.now() - started > maxMs) {
          resolve();
          return;
        }
        window.setTimeout(tick, 80);
      };
      tick();
    });
  }, []);

  const requestClose = useCallback(() => {
    const jid = String(reviewJobId || "").trim();
    const snap = previewRef.current;
    if (jid) {
      if (snap) syncReviewBackgroundPreview(jid, snap);
      // Hand off in-flight creates — do not block leave on them.
      detachReviewBackground(jid, snap);
      // Also copy any RAG ids not yet mirrored.
      if (deferredRagEntityIdsRef.current.size) {
        trackReviewBackgroundRagIds(jid, ...deferredRagEntityIdsRef.current);
        deferredRagEntityIdsRef.current.clear();
      }
    } else {
      // No job id — best-effort wait (legacy path).
      setClosingReview(true);
      void (async () => {
        try {
          await waitForCatalogSavesIdle();
          flushDeferredRagSummaries();
          await flushReviewPersist();
        } finally {
          setClosingReview(false);
          onClose?.();
        }
      })();
      return;
    }
    onClose?.();
  }, [
    flushDeferredRagSummaries,
    flushReviewPersist,
    onClose,
    reviewJobId,
    waitForCatalogSavesIdle,
  ]);

  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;
  const requestCloseStable = useCallback(() => {
    requestCloseRef.current?.();
  }, []);

  const applyPreviewPatchRef = useRef(applyPreviewPatch);
  applyPreviewPatchRef.current = applyPreviewPatch;

  const lastSaveBusyNotifyRef = useRef(null);

  useEffect(() => {
    if (!open) lastSaveBusyNotifyRef.current = null;
  }, [open]);

  const openedReviewJobRef = useRef(null);
  const autoCompleteResolvedStructureLinksRef = useRef(null);

  const [bgSaveTick, setBgSaveTick] = useState(0);

  useEffect(() => {
    const jid = String(reviewJobId || "").trim();
    if (!jid) return undefined;
    return subscribeReviewBackground(jid, () => {
      setBgSaveTick((t) => t + 1);
    });
  }, [reviewJobId]);

  useEffect(() => {
    const jid = String(reviewJobId || preview?.job_id || "").trim();
    if (jid && preview) {
      syncReviewBackgroundPreview(jid, preview);
      if (open) attachReviewBackground(jid, preview);
    }
  }, [reviewJobId, preview, open]);

  useEffect(() => {
    if (!open || !preview) {
      openedReviewJobRef.current = null;
      return;
    }
    const jid = String(reviewJobId || preview?.job_id || "").trim();
    if (!jid || openedReviewJobRef.current === jid) return;
    openedReviewJobRef.current = jid;
    persistFlushDoneRef.current = false;
    const cleaned = stripStaleInProgressReviewOutcomes(preview, { jobId: jid });
    const pruned = pruneDeadReviewCards(cleaned);
    if (pruned !== preview) {
      applyPreviewPatchRef.current?.(
        () => pruned,
        {
          action:
            pruned !== cleaned
              ? "prune_dead_review_cards"
              : "clear_stale_in_progress_outcomes",
        },
        { persistImmediately: false }
      );
    }
    const hydrated = hydrateReviewActionsFromPreview(pruned);
    setRelActions(hydrated.relActions);
    setCatalogActions(hydrated.catalogActions);
    setMatchActions(hydrated.matchActions);
    setRosterRowActions(hydrated.rosterRowActions);
    if (hydrated.descriptionApplied) {
      setDescAccepted(true);
      setDescSkipped(false);
    } else if (hydrated.descriptionDiscarded) {
      setDescAccepted(false);
      setDescSkipped(true);
    }
    deferredRagEntityIdsRef.current.clear();
    verifiedEntityIdsRef.current.clear();
  }, [open, preview?.job_id, reviewJobId]);

  useEffect(() => {
    if (open) return undefined;
    flushDeferredRagSummaries();
    void flushReviewPersist();
    return () => {
      flushDeferredRagSummaries();
      void flushReviewPersist();
    };
  }, [open, flushDeferredRagSummaries, flushReviewPersist]);

  const previewRef = useRef(preview);
  const metamodelSyncSkipDebounceUntilRef = useRef(0);
  const relActionsRef = useRef(relActions);

  const resolution = Array.isArray(preview?.resolution) ? preview.resolution : [];
  const proposed = Array.isArray(preview?.proposed_relationships)
    ? preview.proposed_relationships
    : [];
  const catalogStubs = Array.isArray(preview?.proposed_catalog_entities)
    ? preview.proposed_catalog_entities
    : [];
  const anchorAttrs = preview?.anchor_attributes || {};
  const descProposal = preview?.description_proposal || null;

  const anchorProvenance = useMemo(
    () => buildAnchorProvenance(preview, editingEntity),
    [preview, editingEntity]
  );

  const entityRosterEntries = useMemo(
    () => buildEnrichmentEntityRoster(preview, editingEntity),
    [preview, editingEntity]
  );

  const missingCatalogSignals = useMemo(() => {
    const unmapped = resolution.filter(
      (r) => r && r.kind && r.kind !== "linked_existing"
    );
    const unresolvedEdges = proposed.filter((r) => !isResolvable(r));
    return {
      showBanner: unmapped.length > 0 || unresolvedEdges.length > 0,
      unmappedCount: unmapped.length,
      unresolvedEdgeCount: unresolvedEdges.length,
    };
  }, [resolution, proposed]);

  const potentialMatches = useMemo(() => {
    return resolution.filter((r) =>
      isNameMatchReviewRow(r, preview, editingEntity)
    );
  }, [resolution, preview, editingEntity]);

  const unresolvedPotentialMatches = useMemo(
    () =>
      potentialMatches.filter((row) => {
        const rk = resolutionKey(row);
        const act = effectiveMatchAction(preview, rk, matchActions);
        return (
          act?.state !== "confirmed" &&
          act?.state !== "kept_new" &&
          act?.state !== "discarded"
        );
      }),
    [potentialMatches, matchActions, preview]
  );
  const interfaceRelatedCount = useMemo(
    () => proposed.filter((r) => isInterfaceKind(r?.kind || "")).length,
    [proposed]
  );
  const detectedInterfaceSuggestions = useMemo(() => {
    const toolResults = Array.isArray(preview?.tool_results) ? preview.tool_results : [];
    const row = toolResults.find((t) => t?.tool_id === "interfaces_flow");
    const list = row?.raw?.suggested_interfaces;
    return Array.isArray(list) ? list.length : 0;
  }, [preview?.tool_results]);
  const interfaceDiagnostics = useMemo(() => {
    const total = catalogStubs.length;
    const existing = catalogStubs.filter((row) =>
      Boolean(String(row?.existing_interface_id || "").trim())
    ).length;
    const needsCreation = total - existing;
    return { total, existing, needsCreation };
  }, [catalogStubs]);

  // Stable per proposed row — do not include entity ids (they are filled in after "Create entity +
  // approve") or the key changes and relActions no longer matches, briefly showing a clickable Approve.
  const relationKey = (_rel, idx) => `p${idx}`;

  const unresolvedRelationshipIdxSet = useMemo(() => {
    const s = new Set();
    const rows = Array.isArray(proposed) ? proposed : [];
    rows.forEach((rel, idx) => {
      if (HIDDEN_REVIEW_REL_KINDS.has(String(rel?.kind || "").trim())) return;
      const eff = effectiveRelReviewState(preview, rel, idx, relActions);
      const state = eff?.state;
      if (state === "approved" || state === "discarded") return;
      if (String(rel?.existing_relationship_id || "").trim()) return;
      if (!isResolvable(rel)) s.add(idx);
    });
    return s;
  }, [proposed, preview, relActions]);

  const unresolvedRelationshipHints = useMemo(() => {
    const rows = Array.isArray(proposed) ? proposed : [];
    const anchorType =
      preview?.anchor_entity_type || editingEntity?.type || "application";
    const typeWord = String(anchorType)
      .replace(/_/g, "-")
      .replace(/-/g, " ");
    const anchor = editingEntity?.name || `This ${typeWord}`;
    const out = [];
    rows.forEach((rel, idx) => {
      if (!unresolvedRelationshipIdxSet.has(idx)) return;
      out.push({
        idx,
        kind: String(rel?.kind || rel?.relationship_type || "relationship"),
        summary: relationshipSentence(anchor, rel, anchorType),
      });
    });
    return out;
  }, [
    proposed,
    unresolvedRelationshipIdxSet,
    editingEntity?.name,
    editingEntity?.type,
    preview?.anchor_entity_type,
  ]);

  const rosterReviewComplete = useMemo(() => {
    const entries = Array.isArray(entityRosterEntries) ? entityRosterEntries : [];
    if (entries.length === 0) return true;
    const relRows = Array.isArray(proposed) ? proposed : [];

    const rosterRowDone = (key) => {
      const rs = effectiveRosterRowState(preview, key, rosterRowActions);
      return rs.state === "approved" || rs.state === "discarded";
    };

    const proposedTouchingDone = (key) => {
      const indices = proposedIndicesTouchingRosterKey(key, relRows, preview, editingEntity);
      if (!indices.length) return null;
      return indices.every((idx) => {
        const eff = effectiveRelReviewState(preview, relRows[idx], idx, relActions);
        return eff?.state === "approved" || eff?.state === "discarded";
      });
    };

    const resolutionMatchDone = (pk) => {
      const act = effectiveMatchAction(preview, pk, matchActions);
      return (
        act?.state === "confirmed" ||
        act?.state === "kept_new" ||
        act?.state === "discarded"
      );
    };

    for (const entry of entries) {
      if (rosterRowDone(entry.key)) continue;

      const touchDone = proposedTouchingDone(entry.key);
      if (touchDone === true) continue;

      if (touchDone === null && entry.category === "resolution") {
        const pk = entry.key.startsWith("resolution:")
          ? entry.key.slice("resolution:".length)
          : "";
        if (pk && resolutionMatchDone(pk)) continue;
      }

      return false;
    }
    return true;
  }, [
    entityRosterEntries,
    preview,
    rosterRowActions,
    proposed,
    relActions,
    matchActions,
    editingEntity,
  ]);

  const enrichmentReviewComplete = useMemo(() => {
    if (!rosterReviewComplete) return false;
    if (unresolvedPotentialMatches.length > 0) return false;
    const rows = Array.isArray(proposed) ? proposed : [];
    for (let idx = 0; idx < rows.length; idx++) {
      if (!isProposedRelationshipRowComplete(preview, rows[idx], idx, relActions)) {
        return false;
      }
    }
    const stubs = Array.isArray(catalogStubs) ? catalogStubs : [];
    for (let idx = 0; idx < stubs.length; idx++) {
      if (!isCatalogStubComplete(preview, stubs[idx], idx, catalogActions)) {
        return false;
      }
    }
    return true;
  }, [
    rosterReviewComplete,
    unresolvedPotentialMatches,
    proposed,
    preview,
    relActions,
    catalogStubs,
    catalogActions,
  ]);

  const reviewProgress = useMemo(() => {
    const rows = Array.isArray(proposed) ? proposed : [];
    let total = 0;
    let approved = 0;
    let discarded = 0;
    let unresolved = 0;
    rows.forEach((rel, idx) => {
      if (HIDDEN_REVIEW_REL_KINDS.has(String(rel?.kind || "").trim())) return;
      total += 1;
      const eff = effectiveRelReviewState(preview, rel, idx, relActions);
      const state = eff?.state;
      if (state === "approved") {
        approved += 1;
      } else if (state === "discarded") {
        discarded += 1;
      } else if (!isResolvable(rel)) {
        unresolved += 1;
      }
    });
    const reviewed = approved + discarded;
    const percent = total > 0 ? Math.round((reviewed / total) * 100) : 0;
    return {
      total,
      approved,
      discarded,
      unresolved,
      reviewed,
      percent,
      resolved: Math.max(0, total - unresolved),
    };
  }, [proposed, relActions, preview]);

  previewRef.current = preview;
  matchActionsRef.current = matchActions;
  relActionsRef.current = relActions;

  useEffect(() => {
    if (!open || !preview) return;
    let cancelled = false;
    void loadEntityTypeOptionsOnce()
      .then((list) => {
        if (!cancelled) setEntityTypeOptions(list);
      })
      .catch((e) => {
        console.error(e);
      });
    return () => {
      cancelled = true;
    };
  }, [open, preview?.job_id, reviewJobId]);

  // Modal mode: one overlay scrollbar only — lock page scroll behind the dialog.
  useEffect(() => {
    if (!open || inline) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, inline]);

  const confirmExistingEntityMatch = useCallback(
    async ({
      candidate,
      surfaceName,
      entityType,
      resolutionRow = null,
      action = "confirm_existing_entity",
    }) => {
      const cid = String(candidate?.id || "").trim();
      const resolvedSurfaceName = String(surfaceName || resolutionRow?.surface_name || "").trim();
      const resolvedEntityType = String(
        entityType || resolutionRow?.entity_type || candidate?.entity_type || candidate?.type || ""
      ).trim();
      if (!cid || !resolvedSurfaceName || !resolvedEntityType) {
        throw new Error("No entity selected");
      }

      const matchedResolutionRow =
        resolutionRow ||
        findResolutionRow(previewRef.current || preview, resolvedSurfaceName, resolvedEntityType);
      const resolutionKeyValue = matchedResolutionRow
        ? resolutionKey({
            surface_name: resolvedSurfaceName,
            entity_type: resolvedEntityType,
          })
        : "";

      if (resolutionKeyValue) {
        setMatchActions((prev) => ({
          ...prev,
          [resolutionKeyValue]: {
            ...(prev[resolutionKeyValue] || {}),
            state: "confirming",
            error: "",
          },
        }));
      }

      const aliasToAdd = String(resolvedSurfaceName || "").trim();
      const aliasMerged = mergeAliasNames(candidate?.alias_names, aliasToAdd);
      // Alias writes must not wait on in-flight catalog creates — apply the
      // match immediately so Choose existing stays usable while cards save.
      if (
        !deferMapWrites &&
        aliasMerged !== String(candidate?.alias_names || "")
      ) {
        void api
          .updateEntity(cid, { alias_names: aliasMerged }, deferRag)
          .then(() => {
            trackDeferredRagEntities(cid);
          })
          .catch((e) => {
            console.error("Could not update alias for matched entity:", e);
          });
      }

      applyPreviewPatch(
        (p) => {
          let next = patchPreviewAfterExistingMatch(p, {
            surfaceName: resolvedSurfaceName,
            entityType: resolvedEntityType,
            entityId: cid,
          });
          if (resolutionKeyValue) {
            next = setResolutionMatchOutcome(next, resolutionKeyValue, "confirmed");
          }
          return next;
        },
        {
          action,
          resolution_key: resolutionKeyValue || undefined,
          entity_id: cid,
          entity_name: candidate?.name || resolvedSurfaceName,
          entity_type: resolvedEntityType,
          alias_added: aliasToAdd || null,
        }
      );

      if (resolutionKeyValue) {
        setMatchActions((prev) => ({
          ...prev,
          [resolutionKeyValue]: {
            ...(prev[resolutionKeyValue] || {}),
            state: "confirmed",
            error: "",
          },
        }));
      }

      void autoCompleteResolvedStructureLinksRef.current?.();
      return cid;
    },
    [applyPreviewPatch, deferMapWrites, deferRag, preview, trackDeferredRagEntities]
  );

  const handleUseExistingCandidate = useCallback(
    async (candidate, missingSurfaceName, missingEntityType) => {
      await confirmExistingEntityMatch({
        candidate,
        surfaceName: missingSurfaceName,
        entityType: missingEntityType,
        action: "use_existing_catalog_entity",
      });
    },
    [confirmExistingEntityMatch]
  );

  const handleApproveRel = useCallback(
    async (rel, idx) => {
      setGlobalActionError("");
      const key = relationKey(rel, idx);
      const eff = effectiveRelReviewState(preview, rel, idx, relActions);
      if (eff?.state === "approved" || eff?.state === "discarded") {
        return;
      }
      if (eff?.state === "approving") {
        if (catalogSaveInFlightRef.current > 0) {
          return;
        }
        applyPreviewPatch(
          (p) => clearProposedRelationshipOutcome(p, key),
          { action: "clear_stale_relationship_approving", proposed_key: key },
          { persistImmediately: true }
        );
        setRelActions((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }

      const relKind = String(rel?.kind || "").trim();
      if (isInterfaceKind(relKind)) {
        const errText =
          "Interface integrations are approved from the interface card in the queue, not as a generic relationship.";
        setRelActions((prev) => ({
          ...prev,
          [key]: { ...(prev[key] || {}), state: "error", error: errText },
        }));
        setGlobalActionError(errText);
        return;
      }

      // Opening Session: record the steward decision; publish writes the Approved baseline.
      if (deferMapWrites) {
        setRelActions((prev) => ({
          ...prev,
          [key]: {
            ...(prev[key] || {}),
            state: "approved",
            error: "",
            deferred: true,
          },
        }));
        applyPreviewPatch(
          (p) => setProposedRelationshipOutcome(p, key, "approved"),
          {
            action: "approve_proposed_relationship_deferred",
            proposed_key: key,
          },
          { persistImmediately: true }
        );
        return;
      }

      // Obsolete catalog edges: user confirms delete (never auto-removed).
      if (isObsoleteRel(rel)) {
        const rid = String(rel?.relationship_id || "").trim();
        if (!rid) {
          const errText = "This obsolete connection has no relationship id to remove.";
          setRelActions((prev) => ({
            ...prev,
            [key]: { ...(prev[key] || {}), state: "error", error: errText },
          }));
          setGlobalActionError(errText);
          return;
        }
        beginCatalogSave();
        setRelActions((prev) => ({
          ...prev,
          [key]: { ...(prev[key] || {}), state: "approving", error: "" },
        }));
        applyPreviewPatch(
          (p) => setProposedRelationshipOutcome(p, key, "approving"),
          { action: "approve_obsolete_start", proposed_key: key, relationship_id: rid }
        );
        try {
          await api.deleteRelationship(rid);
          setRelActions((prev) => ({
            ...prev,
            [key]: {
              ...(prev[key] || {}),
              state: "approved",
              error: "",
              relationshipId: rid,
              removed: true,
            },
          }));
          applyPreviewPatch(
            (p) => {
              let next = setProposedRelationshipOutcome(p, key, "approved");
              const outcomes = { ...(next.enrichment_review_outcomes || {}) };
              const ids = Array.isArray(outcomes.obsolete_relationship_ids)
                ? [...outcomes.obsolete_relationship_ids]
                : [];
              if (!ids.includes(rid)) ids.push(rid);
              next = {
                ...next,
                enrichment_review_outcomes: {
                  ...outcomes,
                  obsolete_relationship_ids: ids,
                },
              };
              return next;
            },
            {
              action: "remove_obsolete_relationship",
              proposed_key: key,
              relationship_id: rid,
            },
            { persistImmediately: true }
          );
        } catch (err) {
          const errText = extractActionErrorMessage(
            err,
            "Could not remove this connection from the map."
          );
          setRelActions((prev) => ({
            ...prev,
            [key]: { ...(prev[key] || {}), state: "error", error: errText },
          }));
          applyPreviewPatch(
            (p) => clearProposedRelationshipOutcome(p, key),
            { action: "approve_obsolete_error", proposed_key: key }
          );
          setGlobalActionError(errText);
        } finally {
          endCatalogSave();
        }
        return;
      }

      beginCatalogSave();
      const anchorId = String(
        preview?.anchor_application_id || editingEntity?.id || ""
      ).trim();
      let relEff = normalizeEnrichmentRelationshipForPersist(
        augmentRelMissingEndpoint(applyRelationshipDisplayOverride(rel, preview, idx)),
        anchorId
      );
      let sourceId = String(relEff?.source_entity_id || "").trim();
      let targetId = String(relEff?.target_entity_id || "").trim();
      let relType = String(relEff?.relationship_type || "").trim();

      if (
        (!sourceId || !targetId) &&
        Array.isArray(relEff?.candidate_matches) &&
        relEff.candidate_matches.length > 0
      ) {
        const cand = relEff.candidate_matches[0];
        const effectiveMissingType = missingEndpointEntityTypeForCreate(
          relEff,
          previewRef.current || preview,
          editingEntity
        );
        const cid = String(cand?.id || "").trim();
        if (
          cid &&
          String(relEff?.missing_surface_name || "").trim() &&
          String(effectiveMissingType || relEff?.missing_entity_type || "").trim()
        ) {
          await handleUseExistingCandidate(
            cand,
            relEff.missing_surface_name,
            effectiveMissingType || relEff.missing_entity_type
          );
          const freshRel =
            (previewRef.current?.proposed_relationships || [])[idx] || rel;
          relEff = normalizeEnrichmentRelationshipForPersist(
            augmentRelMissingEndpoint(
              applyRelationshipDisplayOverride(freshRel, previewRef.current, idx)
            ),
            anchorId
          );
          sourceId = String(relEff?.source_entity_id || "").trim();
          targetId = String(relEff?.target_entity_id || "").trim();
          relType = String(relEff?.relationship_type || "").trim();
        }
      }

      const verifyEntityId = async (id) => {
        const trimmed = String(id || "").trim();
        if (!trimmed) return "";
        if (verifiedEntityIdsRef.current.has(trimmed)) return trimmed;
        try {
          await api.getEntity(trimmed, {
            includeUnapproved: true,
            bypassCache: true,
          });
          verifiedEntityIdsRef.current.add(trimmed);
          return trimmed;
        } catch {
          return "";
        }
      };

      setRelActions((prev) => ({
        ...prev,
        [key]: { ...(prev[key] || {}), state: "approving", error: "" },
      }));
      applyPreviewPatch(
        (p) => setProposedRelationshipOutcome(p, key, "approving"),
        { action: "approve_proposed_relationship_started", proposed_key: key },
        { persistImmediately: false }
      );
      try {
        const [src, tgt] = await Promise.all([
          verifyEntityId(sourceId),
          verifyEntityId(targetId),
        ]);
        sourceId = src;
        targetId = tgt;
        relEff = normalizeEnrichmentRelationshipForPersist(
          augmentRelMissingEndpoint({
            ...relEff,
            source_entity_id: sourceId || null,
            target_entity_id: targetId || null,
          }),
          anchorId
        );
        sourceId = String(relEff?.source_entity_id || "").trim();
        targetId = String(relEff?.target_entity_id || "").trim();
        relType = String(relEff?.relationship_type || "").trim();

        const missingName = String(relEff?.missing_surface_name || "").trim();
        const missingType = missingEndpointEntityTypeForCreate(
          relEff,
          previewRef.current || preview,
          editingEntity
        );
        if ((!sourceId || !targetId) && missingName && missingType) {
          if (normalizeTypeForCompare(missingType) === "interface") {
            throw new Error(
              "Interface integrations are approved from the interface card in the queue, not as a generic relationship."
            );
          }
          const createdEntity = await api.createEntity(
            {
              name: missingName,
              type: missingType,
              approval_status: "Proposed",
              description: entityCatalogDescription(relEff, {
                entityType: missingType,
                entityName: missingName,
              }),
              confidence: 0.85,
              ...buildEvidenceAcceptPayload(
                preview,
                reviewJobId,
                evidenceQuoteFromRow(relEff)
              ),
            },
            deferRag
          );
          const createdId = String(createdEntity?.id || "").trim();
          if (createdId) verifiedEntityIdsRef.current.add(createdId);
          trackDeferredRagEntities(createdId);
          if (relEff?.missing_end === "source" && createdId) {
            sourceId = createdId;
          } else if (relEff?.missing_end === "target" && createdId) {
            targetId = createdId;
          }
          applyPreviewPatch(
            (p) =>
              patchPreviewAfterCatalogCreate(p, createdEntity, {
                entityType: missingType,
              }),
            {
              action: "create_catalog_entity",
              entity_id: createdId,
              entity_name: missingName,
              entity_type: missingType,
              context: "relationship_missing_endpoint",
              proposed_key: key,
            }
          );
          relEff = normalizeEnrichmentRelationshipForPersist(
            augmentRelMissingEndpoint({
              ...relEff,
              source_entity_id: sourceId || null,
              target_entity_id: targetId || null,
            }),
            anchorId
          );
          sourceId = String(relEff?.source_entity_id || "").trim();
          targetId = String(relEff?.target_entity_id || "").trim();
          relType = String(relEff?.relationship_type || "").trim();
          if (relEff?.missing_end === "source") {
            sourceId = createdId || sourceId;
            targetId = await verifyEntityId(targetId);
          } else {
            targetId = createdId || targetId;
            sourceId = await verifyEntityId(sourceId);
          }
        }

        if (!sourceId || !targetId) {
          throw new Error(
            "One side is not on your map yet — match the name first or pick a match below before approving."
          );
        }
        if (!relType) {
          throw new Error(
            "This suggestion has no connection type. Skip it or upload again."
          );
        }

        const persistType = relEff?.relationship_type || relEff?.type || relType;
        const anchorName =
          editingEntity?.name || preview?.anchor_name || "Application";
        const rationale = buildRationale(relEff, anchorName);
        const descParts = [rationale];
        const compoundType = String(rel?.relationship_type || "").trim();
        if (compoundType && compoundType !== persistType) {
          descParts.push(`Enrichment relationship: ${compoundType}`);
        }
        let relationshipId = null;
        try {
          const created = await api.createRelationship(
            {
              source_entity_id: sourceId,
              target_entity_id: targetId,
              type: persistType,
              status: "Approved",
              description: descParts.filter(Boolean).join("\n\n"),
              confidence: 0.9,
              ...buildEvidenceAcceptPayload(
                preview,
                reviewJobId,
                evidenceQuoteFromRow(relEff)
              ),
            },
            deferRag
          );
          relationshipId =
            created?.id ||
            created?.relationship?.id ||
            created?.relationship_id ||
            null;
        } catch (relErr) {
          if (!isDuplicateRelationshipError(relErr)) {
            throw relErr;
          }
        }
        trackDeferredRagEntities(sourceId, targetId, anchorId);
        const row = relationshipId
          ? {
              id: relationshipId,
              source_entity_id: sourceId,
              target_entity_id: targetId,
              type: persistType,
              status: "Approved",
              description: rationale,
            }
          : null;
        if (row && typeof onRelationshipsApplied === "function") {
          onRelationshipsApplied([row]);
        }
        setRelActions((prev) => ({
          ...prev,
          [key]: {
            ...(prev[key] || {}),
            relationshipId,
            state: "approved",
            error: "",
          },
        }));
        applyPreviewPatch(
          (p) => {
            const patched = relationshipId
              ? patchPreviewAfterRelationshipApprove(
                  p,
                  { ...relEff, source_entity_id: sourceId, target_entity_id: targetId },
                  relationshipId
                )
              : p;
            return setProposedRelationshipOutcome(patched, key, "approved");
          },
          {
            action: "approve_proposed_relationship",
            proposed_key: key,
            relationship_id: relationshipId,
            relationship_type: persistType,
            ...relationshipAuditFields(relEff, editingEntity?.name || preview?.anchor_name),
          },
          { persistImmediately: true }
        );
      } catch (e) {
        const errText = extractActionErrorMessage(e, "Approve failed");
        applyPreviewPatch(
          (p) => clearProposedRelationshipOutcome(p, key),
          { action: "approve_proposed_relationship_failed", proposed_key: key },
          { persistImmediately: true }
        );
        setRelActions((prev) => ({
          ...prev,
          [key]: {
            ...(prev[key] || {}),
            state: "error",
            error: errText,
          },
        }));
        setGlobalActionError(`Could not approve this recommendation: ${errText}`);
      } finally {
        endCatalogSave();
      }
    },
    [
      applyPreviewPatch,
      beginCatalogSave,
      deferMapWrites,
      deferRag,
      editingEntity?.name,
      endCatalogSave,
      editingEntity,
      handleUseExistingCandidate,
      onRelationshipsApplied,
      preview,
      relActions,
      trackDeferredRagEntities,
    ]
  );

  const handleDiscardRel = useCallback(
    (rel, idx) => {
      const key = relationKey(rel, idx);
      const relEff = augmentRelMissingEndpoint(rel);
      const anchorName = editingEntity?.name || preview?.anchor_name || "Application";
      const auditCtx = relationshipAuditFields(rel, anchorName);
      const missType = normName(relEff?.missing_entity_type);
      const missName = normName(relEff?.missing_surface_name);
      const keysToMark = new Set([key]);
      if (missType && missName) {
        proposed.forEach((r2, i2) => {
          const r2e = augmentRelMissingEndpoint(r2);
          if (
            normName(r2e?.missing_entity_type) === missType &&
            normName(r2e?.missing_surface_name) === missName
          ) {
            keysToMark.add(relationKey(r2, i2));
          }
        });
      }
      applyPreviewPatch((p) => {
        let next = p;
        keysToMark.forEach((k) => {
          next = setProposedRelationshipOutcome(next, k, "discarded");
        });
        return next;
      }, {
        action: "discard_proposed_relationship",
        proposed_keys: Array.from(keysToMark),
        ...auditCtx,
      }, { persistImmediately: true });
      setRelActions((prev) => {
        const next = { ...prev };
        keysToMark.forEach((k) => {
          next[k] = { ...(next[k] || {}), state: "discarded", error: "" };
        });
        return next;
      });
    },
    [applyPreviewPatch, proposed, preview, editingEntity?.name]
  );

  const catalogRowKey = catalogStubKey;

  const ensureApplicationEntity = useCallback(
    async ({ roleLabel, name, existingId, row, suggestedType = "application" }) => {
      const currentId = String(existingId || "").trim();
      if (currentId) return currentId;
      const entityName = String(name || "").trim();
      if (!entityName) {
        throw new Error(`${roleLabel} application is missing from this suggestion.`);
      }
      const entityType =
        effectiveCatalogSurfaceType(
          previewRef.current || preview,
          entityName,
          suggestedType || "application",
          editingEntity
        ) || "application";
      if (normalizeTypeForCompare(entityType) !== "application") {
        throw new Error(
          `${roleLabel} is no longer an application (${entityType}); this connection cannot be added.`
        );
      }
      const created = await api.createEntity(
        {
          name: entityName,
          type: "application",
          approval_status: "Proposed",
          description: entityCatalogDescription(row, {
            entityType: "application",
            entityName,
          }),
          confidence: 0.85,
          ...buildEvidenceAcceptPayload(
            previewRef.current || preview,
            reviewJobId,
            evidenceQuoteFromRow(row)
          ),
        },
        deferRag
      );
      applyPreviewPatch((p) =>
        patchPreviewAfterCatalogCreate(p, created, {
          entityType: "application",
        })
      );
      const createdId = String(created?.id || "").trim();
      trackDeferredRagEntities(createdId);
      if (!createdId) {
        throw new Error(`${roleLabel} application could not be created.`);
      }
      return createdId;
    },
    [
      applyPreviewPatch,
      deferRag,
      editingEntity,
      preview,
      reviewJobId,
      trackDeferredRagEntities,
    ]
  );

  const ensureInterfaceRelationships = useCallback(
    async ({ providerId, consumerId, interfaceId, row, anchorName }) => {
      const rationale = relationshipAssociationDescription(row, { anchorName });
      const evidencePayload = buildEvidenceAcceptPayload(
        previewRef.current || preview,
        reviewJobId,
        evidenceQuoteFromRow(row)
      );

      const createOne = async (source_entity_id, target_entity_id, type) => {
        try {
          const created = await api.createRelationship(
            {
              source_entity_id,
              target_entity_id,
              type,
              status: "Approved",
              description: rationale,
              confidence: 0.9,
              ...evidencePayload,
            },
            deferRag
          );
          trackDeferredRagEntities(source_entity_id, target_entity_id);
          const relationshipId = created?.id || created?.relationship?.id || null;
          if (relationshipId && typeof onRelationshipsApplied === "function") {
            onRelationshipsApplied([
              {
                id: relationshipId,
                source_entity_id,
                target_entity_id,
                type,
                status: "Approved",
                description: rationale,
              },
            ]);
          }
        } catch (e) {
          if (!isDuplicateRelationshipError(e)) {
            throw e;
          }
        }
      };

      await Promise.all([
        createOne(providerId, interfaceId, "provides"),
        createOne(consumerId, interfaceId, "consumes"),
      ]);
    },
    [
      deferRag,
      onRelationshipsApplied,
      preview,
      reviewJobId,
      trackDeferredRagEntities,
    ]
  );

  const beginCatalogApprove = useCallback(
    (row, idx, { startedAction, clearAction = "clear_stale_catalog_creating" }) => {
      const ck = catalogRowKey(row, idx);
      const cact = effectiveCatalogAction(preview, ck, catalogActions);
      if (cact.state === "created" || cact.state === "discarded") {
        return { ck, skip: true };
      }
      const po = preview?.enrichment_review_outcomes?.catalog_entities?.[ck];
      if (cact.state === "creating" || po === "creating") {
        if (catalogSaveInFlightRef.current > 0) {
          return { ck, skip: true };
        }
        applyPreviewPatch(
          (p) => clearCatalogEntityOutcome(p, ck),
          { action: clearAction, catalog_key: ck },
          { persistImmediately: true }
        );
        setCatalogActions((prev) => {
          const next = { ...prev };
          delete next[ck];
          return next;
        });
      }
      beginCatalogSave();
      setCatalogActions((prev) => ({
        ...prev,
        [ck]: { ...(prev[ck] || {}), state: "creating", error: "" },
      }));
      applyPreviewPatch(
        (p) => setCatalogEntityOutcome(p, ck, "creating"),
        { action: startedAction, catalog_key: ck },
        { persistImmediately: false }
      );
      return { ck, skip: false };
    },
    [applyPreviewPatch, beginCatalogSave, catalogActions, catalogRowKey, preview]
  );

  const handleAddInterfaceStub = useCallback(
    async (row, idx, opts = {}) => {
      setGlobalActionError("");
      const ck = catalogRowKey(row, idx);
      const name = String(row?.suggested_name || "").trim();
      if (!name) {
        const errText = "Interface name is missing from this suggestion.";
        setCatalogActions((prev) => ({
          ...prev,
          [ck]: { ...(prev[ck] || {}), state: "error", error: errText },
        }));
        setGlobalActionError(errText);
        return;
      }
      const cact = effectiveCatalogAction(preview, ck, catalogActions);
      if (cact.state === "created" || cact.state === "discarded") {
        return;
      }
      const po = preview?.enrichment_review_outcomes?.catalog_entities?.[ck];
      if (cact.state === "creating" || po === "creating") {
        if (catalogSaveInFlightRef.current > 0) {
          return;
        }
        applyPreviewPatch(
          (p) => clearCatalogEntityOutcome(p, ck),
          { action: "clear_stale_catalog_creating", catalog_key: ck },
          { persistImmediately: true }
        );
        setCatalogActions((prev) => {
          const next = { ...prev };
          delete next[ck];
          return next;
        });
      }
      const wiredRow = alignInterfaceRowWithDataFlow(applyCatalogEndpointCandidates(row));
      const providerWired =
        String(row?.provider_entity_id || "").trim() !==
        String(wiredRow?.provider_entity_id || "").trim();
      const consumerWired =
        String(row?.consumer_entity_id || "").trim() !==
        String(wiredRow?.consumer_entity_id || "").trim();
      if (providerWired || consumerWired) {
        applyPreviewPatch(
          (p) => {
            if (!p) return p;
            const stubs = [...(p.proposed_catalog_entities || [])];
            if (stubs[idx]) stubs[idx] = wiredRow;
            return { ...p, proposed_catalog_entities: stubs };
          },
          {
            action: "wire_catalog_endpoints_from_candidates",
            catalog_key: ck,
          }
        );
      }

      if (deferMapWrites) {
        setCatalogActions((prev) => ({
          ...prev,
          [ck]: {
            ...(prev[ck] || {}),
            state: "created",
            error: "",
            deferred: true,
          },
        }));
        applyPreviewPatch(
          (p) => setCatalogEntityOutcome(p, ck, "created"),
          {
            action: "approve_interface_suggestion_deferred",
            catalog_key: ck,
          },
          { persistImmediately: true }
        );
        return;
      }

      beginCatalogSave();
      setCatalogActions((prev) => ({
        ...prev,
        [ck]: { ...(prev[ck] || {}), state: "creating", error: "" },
      }));
      applyPreviewPatch(
        (p) => setCatalogEntityOutcome(p, ck, "creating"),
        { action: "approve_interface_suggestion_started", catalog_key: ck },
        { persistImmediately: false }
      );
      try {
        const livePreview = previewRef.current || preview;
        if (
          !interfaceStubEndpointsAreApplications(
            wiredRow,
            livePreview,
            editingEntity
          )
        ) {
          applyPreviewPatch(
            (p) => setCatalogEntityOutcome(p, ck, "discarded"),
            {
              action: "discard_interface_non_application_endpoints",
              catalog_key: ck,
            },
            { persistImmediately: true }
          );
          setCatalogActions((prev) => ({
            ...prev,
            [ck]: { ...(prev[ck] || {}), state: "discarded", error: "" },
          }));
          endCatalogSave();
          return;
        }
        const forceCreateNew = Boolean(opts?.forceCreateNew);
        const existingInterfaceId = forceCreateNew
          ? ""
          : String(wiredRow?.existing_interface_id || "").trim();
        const usingExisting = Boolean(existingInterfaceId);

        let providerId = String(wiredRow?.provider_entity_id || "").trim();
        let consumerId = String(wiredRow?.consumer_entity_id || "").trim();
        let interfaceId = String(existingInterfaceId || "").trim();
        const anchorName =
          editingEntity?.name || preview?.anchor_name || "Application";
        const rationale = relationshipAssociationDescription(wiredRow, {
          anchorName,
        });

        const ensureEnd = async (role, endName, existingId, suggestedType) =>
          ensureApplicationEntity({
            roleLabel: role,
            name: endName,
            existingId,
            row: wiredRow,
            suggestedType,
          });

        const runSequentialInterface = async () => {
          [providerId, consumerId] = await Promise.all([
            ensureEnd(
              "Source",
              wiredRow?.provider_application_name,
              wiredRow?.provider_entity_id,
              wiredRow?.provider_entity_type || "application"
            ),
            ensureEnd(
              "Target",
              wiredRow?.consumer_application_name,
              wiredRow?.consumer_entity_id,
              wiredRow?.consumer_entity_type || "application"
            ),
          ]);
          if (!providerId || !consumerId) {
            throw new Error(
              "Both endpoints must be on your map before we can add this connection."
            );
          }
          if (!interfaceId) {
            const created = await api.createEntity(
              {
                name,
                type: "interface",
                approval_status: "Proposed",
                description: entityCatalogDescription(wiredRow, {
                  entityType: "interface",
                  entityName: name,
                }),
                confidence: 0.85,
                provider_entity_ids: [providerId],
                consumer_entity_ids: [consumerId],
                ...buildEvidenceAcceptPayload(
                  previewRef.current || preview,
                  reviewJobId,
                  evidenceQuoteFromRow(wiredRow)
                ),
              },
              deferRag
            );
            interfaceId = String(created?.id || "").trim();
            if (!interfaceId) {
              throw new Error("Interface entity could not be created.");
            }
            applyPreviewPatch((p) =>
              patchPreviewAfterCatalogCreate(p, created, {
                entityType: "interface",
                stubKey: wiredRow.interface_suggestion_key,
              })
            );
          }
          await ensureInterfaceRelationships({
            providerId,
            consumerId,
            interfaceId,
            row: wiredRow,
            anchorName,
          });
          trackDeferredRagEntities(providerId, consumerId, interfaceId);
        };

        let usedBatch = false;
        try {
          const missingEnds = [];
          const live = previewRef.current || preview;
          if (!providerId) {
            const endName = String(
              wiredRow?.provider_application_name || ""
            ).trim();
            if (!endName) {
              throw new Error("Source application is missing from this suggestion.");
            }
            const endType =
              effectiveCatalogSurfaceType(
                live,
                endName,
                wiredRow?.provider_entity_type || "application",
                editingEntity
              ) || "application";
            if (normalizeTypeForCompare(endType) !== "application") {
              throw new Error(
                `Source is no longer an application (${endType}); this connection cannot be added.`
              );
            }
            missingEnds.push({
              client_key: "e_prov",
              name: endName,
              type: "application",
              approval_status: "Proposed",
              description: entityCatalogDescription(wiredRow, {
                entityType: "application",
                entityName: endName,
              }),
              confidence: 0.85,
            });
          }
          if (!consumerId) {
            const endName = String(
              wiredRow?.consumer_application_name || ""
            ).trim();
            if (!endName) {
              throw new Error("Target application is missing from this suggestion.");
            }
            const endType =
              effectiveCatalogSurfaceType(
                live,
                endName,
                wiredRow?.consumer_entity_type || "application",
                editingEntity
              ) || "application";
            if (normalizeTypeForCompare(endType) !== "application") {
              throw new Error(
                `Target is no longer an application (${endType}); this connection cannot be added.`
              );
            }
            missingEnds.push({
              client_key: "e_cons",
              name: endName,
              type: "application",
              approval_status: "Proposed",
              description: entityCatalogDescription(wiredRow, {
                entityType: "application",
                entityName: endName,
              }),
              confidence: 0.85,
            });
          }
          const batch = await applyMapWritesForInterface({
            missingEnds,
            interfaceSpec: existingInterfaceId
              ? null
              : {
                  client_key: "e_iface",
                  name,
                  type: "interface",
                  approval_status: "Proposed",
                  description: entityCatalogDescription(wiredRow, {
                    entityType: "interface",
                    entityName: name,
                  }),
                  confidence: 0.85,
                },
            providerId,
            consumerId,
            existingInterfaceId,
            rationale,
            deferRagRegeneration: true,
            evidence: buildEvidenceAcceptPayload(
              previewRef.current || preview,
              reviewJobId,
              evidenceQuoteFromRow(wiredRow)
            ).evidence,
          });
          providerId = batch.providerId;
          consumerId = batch.consumerId;
          interfaceId = batch.interfaceId;
          for (const created of batch.createdEntities) {
            const et = String(created?.type || "").trim();
            applyPreviewPatch((p) =>
              patchPreviewAfterCatalogCreate(p, created, {
                entityType: et || "interface",
                stubKey:
                  et === "interface"
                    ? wiredRow.interface_suggestion_key
                    : undefined,
              })
            );
          }
          trackDeferredRagEntities(...(batch.affectedEntityIds || []));
          if (typeof onRelationshipsApplied === "function") {
            const applied = (batch.relationships || [])
              .filter((r) => r && r.id)
              .map((r) => ({
                id: r.id,
                source_entity_id: r.source_entity_id,
                target_entity_id: r.target_entity_id,
                type: r.type,
                status: "Approved",
                description: rationale,
              }));
            if (applied.length) onRelationshipsApplied(applied);
          }
          usedBatch = true;
        } catch (batchErr) {
          console.warn(
            "mapWrites interface failed; falling back to sequential",
            batchErr
          );
        }
        if (!usedBatch) {
          await runSequentialInterface();
        }

        trackDeferredRagEntities(providerId, consumerId, interfaceId);

        applyPreviewPatch(
          (p) => setCatalogEntityOutcome(p, ck, "created"),
          {
            action: "approve_interface_suggestion",
            catalog_key: ck,
            interface_id: interfaceId,
            entity_name: name,
            entity_type: "interface",
          },
          { persistImmediately: true }
        );

        setCatalogActions((prev) => ({
          ...prev,
          [ck]: {
            ...(prev[ck] || {}),
            state: "created",
            error: "",
            interfaceId,
            usedExistingInterface: usingExisting,
          },
        }));
      } catch (e) {
        const errText = extractActionErrorMessage(e, "Could not add interface");
        applyPreviewPatch(
          (p) => clearCatalogEntityOutcome(p, ck),
          { action: "approve_interface_suggestion_failed", catalog_key: ck },
          { persistImmediately: true }
        );
        setCatalogActions((prev) => ({
          ...prev,
          [ck]: {
            ...(prev[ck] || {}),
            state: "error",
            error: errText,
          },
        }));
        setGlobalActionError(`Create entity + approve failed: ${errText}`);
      } finally {
        endCatalogSave();
      }
    },
    [
      applyPreviewPatch,
      beginCatalogSave,
      catalogActions,
      deferMapWrites,
      deferRag,
      editingEntity,
      endCatalogSave,
      ensureApplicationEntity,
      ensureInterfaceRelationships,
      onRelationshipsApplied,
      preview,
      trackDeferredRagEntities,
    ]
  );

  const autoCompleteBusyRef = useRef(false);

  const autoCompleteReadyCatalogConnections = useCallback(async () => {
    if (deferMapWrites) return;
    if (autoCompleteBusyRef.current) return;
    autoCompleteBusyRef.current = true;
    try {
      // Name-match cards stay clickable during catalog saves (panel unlocks them).
      // Applications are created immediately on keep-new. While name matching
      // continues, only auto-create interface_from_flow when both endpoint ids exist.
      const nameMatchingOpen = () =>
        hasUndecidedNameMatches(
          previewRef.current,
          editingEntity,
          matchActionsRef.current
        );
      for (let wave = 0; wave < 12; wave += 1) {
        const snap = previewRef.current;
        if (!snap) return;
        let ready = listAutoCompletableCatalogStubs(snap, editingEntity);
        if (nameMatchingOpen()) {
          ready = ready.filter((item) => {
            const k = String(item.row?.kind || "").trim();
            if (k !== "interface_from_flow") return false;
            const row = item.row;
            return (
              Boolean(String(row?.provider_entity_id || "").trim()) &&
              Boolean(String(row?.consumer_entity_id || "").trim())
            );
          });
        }
        if (!ready.length) return;
        await mapPool(ready, 3, async (item) => {
          try {
            await handleAddInterfaceStub(item.row, item.idx, {
              forceCreateNew: false,
            });
          } catch (e) {
            console.warn("Auto-complete connection failed:", item.row?.kind, e);
          }
        });
      }
    } finally {
      autoCompleteBusyRef.current = false;
    }
  }, [deferMapWrites, editingEntity, handleAddInterfaceStub]);

  autoCompleteResolvedStructureLinksRef.current =
    autoCompleteReadyCatalogConnections;

  // setState patch + immediate auto-complete can race (updater not flushed yet).
  // Re-run when name-match decisions land OR resolution entity ids appear (e.g.
  // application create after Add as new) — not on review open (hang risk).
  const resolutionMatchSig = useMemo(() => {
    const matches = preview?.enrichment_review_outcomes?.resolution_matches || {};
    const ids = (Array.isArray(preview?.resolution) ? preview.resolution : [])
      .map(
        (r) =>
          `${String(r?.surface_name || "")
            .trim()
            .toLowerCase()}|${String(r?.entity_type || "")
            .trim()
            .toLowerCase()}|${String(r?.entity_id || "").trim()}`
      )
      .sort()
      .join(";");
    return JSON.stringify({ matches, ids });
  }, [preview?.enrichment_review_outcomes?.resolution_matches, preview?.resolution]);
  const resolutionMatchSigSeenRef = useRef(null);
  useEffect(() => {
    if (!open) {
      resolutionMatchSigSeenRef.current = null;
      return undefined;
    }
    if (resolutionMatchSigSeenRef.current === resolutionMatchSig) {
      return undefined;
    }
    resolutionMatchSigSeenRef.current = resolutionMatchSig;
    const t = window.setTimeout(() => {
      void autoCompleteResolvedStructureLinksRef.current?.();
    }, 400);
    return () => window.clearTimeout(t);
  }, [open, resolutionMatchSig]);

  const handleDiscardInterfaceSuggestion = useCallback(
    (row, idx) => {
      const ck = catalogRowKey(row, idx);
      applyPreviewPatch(
        (p) => setCatalogEntityOutcome(p, ck, "discarded"),
        { action: "discard_interface_suggestion", catalog_key: ck },
        { persistImmediately: true }
      );
      setCatalogActions((prev) => ({
        ...prev,
        [ck]: { ...(prev[ck] || {}), state: "discarded", error: "" },
      }));
    },
    [applyPreviewPatch]
  );

  const handleConfirmPotentialMatch = useCallback(
    async (resRow, candidate) => {
      const rk = resolutionKey(resRow);
      setMatchActions((prev) => ({
        ...prev,
        [rk]: { ...(prev[rk] || {}), state: "confirming", error: "" },
      }));
      try {
        await confirmExistingEntityMatch({
          candidate,
          surfaceName: resRow?.surface_name,
          entityType: resRow?.entity_type,
          resolutionRow: resRow,
          action: "confirm_resolution_match",
        });
      } catch (e) {
        setMatchActions((prev) => ({
          ...prev,
          [rk]: {
            ...(prev[rk] || {}),
            state: "error",
            error: e?.message || "Could not confirm match",
          },
        }));
      }
    },
    [confirmExistingEntityMatch]
  );

  const handleKeepAsNew = useCallback(
    async (resRow) => {
      const surfaceName = String(resRow?.surface_name || "").trim();
      const entityType =
        effectiveCatalogSurfaceType(
          previewRef.current || preview,
          surfaceName,
          String(resRow?.entity_type || "").trim() || "application",
          editingEntity
        ) || String(resRow?.entity_type || "").trim();
      const rk = resolutionKey({ surface_name: surfaceName, entity_type: entityType });
      const entityTypeNorm = normalizeTypeForCompare(entityType);

      const keptNewAction = {
        ...(matchActionsRef.current[rk] || {}),
        state: "kept_new",
        error: "",
      };
      matchActionsRef.current = {
        ...matchActionsRef.current,
        [rk]: keptNewAction,
      };
      setMatchActions((prev) => ({
        ...prev,
        [rk]: keptNewAction,
      }));

      applyPreviewPatch(
        (p) =>
          setResolutionMatchOutcome(
            patchPreviewAfterKeepNew(p, {
              surfaceName,
              entityType,
            }),
            rk,
            "kept_new"
          ),
        { action: "keep_new_resolution_match", resolution_key: rk },
        { persistImmediately: false }
      );

      // Applications: create on the map immediately. Solution components: only on
      // explicit SC card approve (stubs stay visible for manual approve).
      const resolutionRows = previewRef.current?.resolution || [];
      const linkedId = String(resRow?.entity_id || "").trim();
      const alreadyLinked =
        Boolean(linkedId) ||
        resolutionRows.some(
          (r) =>
            normName(r?.surface_name) === normName(surfaceName) &&
            normName(r?.entity_type) === normName(entityType) &&
            Boolean(String(r?.entity_id || "").trim())
        );

      if (entityTypeNorm === "application" && !alreadyLinked && !deferMapWrites) {
        beginCatalogSave();
        const creatingAction = {
          ...(matchActionsRef.current[rk] || {}),
          state: "creating",
          error: "",
        };
        matchActionsRef.current = {
          ...matchActionsRef.current,
          [rk]: creatingAction,
        };
        setMatchActions((prev) => ({
          ...prev,
          [rk]: creatingAction,
        }));

        try {
          const created = await api.createEntity(
            {
              name: surfaceName,
              type: entityType,
              approval_status: "Proposed",
              description: entityCatalogDescription(resRow, {
                entityType,
                entityName: surfaceName,
              }),
              confidence: 0.85,
              ...buildEvidenceAcceptPayload(
                previewRef.current || preview,
                reviewJobId,
                evidenceQuoteFromRow(resRow)
              ),
            },
            deferRag
          );
          const createdId = String(created?.id || "").trim();
          if (!createdId) {
            throw new Error("Application could not be created.");
          }
          trackDeferredRagEntities(createdId);
          applyPreviewPatch(
            (p) => {
              let next = patchPreviewAfterCatalogCreate(p, created, {
                entityType,
              });
              next = patchPreviewAfterExistingMatch(next, {
                surfaceName,
                entityType,
                entityId: createdId,
              });
              return setResolutionMatchOutcome(next, rk, "kept_new");
            },
            {
              action: "keep_new_create_application",
              resolution_key: rk,
              entity_id: createdId,
              entity_name: surfaceName,
              entity_type: entityType,
            }
          );
          const successAction = { state: "kept_new", error: "" };
          matchActionsRef.current = {
            ...matchActionsRef.current,
            [rk]: successAction,
          };
          setMatchActions((prev) => ({
            ...prev,
            [rk]: successAction,
          }));
        } catch (e) {
          const errText = extractActionErrorMessage(
            e,
            "Could not create application"
          );
          const errorAction = {
            ...(matchActionsRef.current[rk] || {}),
            state: "kept_new",
            error: errText,
          };
          matchActionsRef.current = {
            ...matchActionsRef.current,
            [rk]: errorAction,
          };
          setMatchActions((prev) => ({
            ...prev,
            [rk]: errorAction,
          }));
          return;
        } finally {
          endCatalogSave();
        }
      }

      void autoCompleteResolvedStructureLinksRef.current?.();
    },
    [
      applyPreviewPatch,
      beginCatalogSave,
      deferMapWrites,
      deferRag,
      editingEntity,
      endCatalogSave,
      preview,
      trackDeferredRagEntities,
    ]
  );

  const handleDiscardPotentialMatch = useCallback(
    (resRow) => {
      const rk = resolutionKey(resRow);
      applyPreviewPatch(
        (p) =>
          setResolutionMatchOutcome(
            patchPreviewAfterDiscardPotentialMatch(p, {
              surfaceName: resRow?.surface_name,
              entityType: resRow?.entity_type,
            }),
            rk,
            "discarded"
          ),
        { action: "discard_resolution_match", resolution_key: rk }
      );
      setMatchActions((prev) => ({
        ...prev,
        [rk]: { ...(prev[rk] || {}), state: "discarded", error: "" },
      }));
      void autoCompleteResolvedStructureLinksRef.current?.();
    },
    [applyPreviewPatch]
  );

  const handleOpenEntityPicker = useCallback((pickerState) => {
    const surfaceName = String(pickerState?.surfaceName || "").trim();
    const entityType = String(pickerState?.entityType || "application").trim();
    if (!surfaceName) return;
    setEntityPickerState({
      ...pickerState,
      surfaceName,
      entityType,
      title:
        pickerState?.title ||
        `Link to existing ${entityType} for ${surfaceName}`,
    });
  }, []);

  const handleCloseEntityPicker = useCallback(() => {
    setEntityPickerState(null);
  }, []);

  const handleSelectEntityFromPicker = useCallback(
    async (entity) => {
      if (!entityPickerState) return;
      const picker = entityPickerState;
      setEntityPickerState(null);
      try {
        await confirmExistingEntityMatch({
          candidate: entity,
          surfaceName: picker.surfaceName,
          entityType: picker.entityType,
          resolutionRow: picker.resolutionRow || null,
          action: "pick_existing_catalog_entity",
        });
      } catch (e) {
        const errText = extractActionErrorMessage(e, "Could not use selected entity");
        setGlobalActionError(errText);
      }
    },
    [confirmExistingEntityMatch, entityPickerState]
  );

  const entityTypeOverridesSig = useMemo(
    () =>
      JSON.stringify(preview?.enrichment_review_outcomes?.entity_type_overrides || {}),
    [preview?.enrichment_review_outcomes?.entity_type_overrides]
  );

  const syncRelationshipsForMetamodel = useCallback(
    async (previewSnapshot, overrides, options = {}) => {
      const pv = previewSnapshot || previewRef.current;
      if (!pv) return;
      const onlyIndices = Array.isArray(options.onlyIndices) ? options.onlyIndices : null;
      const { discarded, repairs, summary } = await reconcileProposedRelationshipsForMetamodel(
        pv,
        overrides,
        editingEntity,
        relActionsRef.current,
        {
          onlyIndices,
          skipDiscards: options.skipDiscards,
        }
      );
      if (!discarded.length && !repairs.length) {
        if (!onlyIndices?.length) setMetamodelSyncNotice(null);
        return;
      }

      const rows = Array.isArray(pv.proposed_relationships) ? pv.proposed_relationships : [];
      const anchorName =
        editingEntity?.name || pv?.anchor_name || preview?.anchor_name || "Application";
      const auditClaims = discarded
        .map((idx) => relationshipAuditFields(rows[idx], anchorName).claim)
        .filter(Boolean);

      applyPreviewPatch(
        (p) =>
          applyMetamodelReconcileResultsToPreview(
            p,
            { discarded, repairs },
            relActionsRef.current
          ),
        onlyIndices?.length
          ? null
          : {
              action: repairs.length
                ? "adjust_proposed_relationships_metamodel"
                : "discard_proposed_relationships_metamodel_invalidation",
              indices: discarded,
              repair_indices: repairs.map((r) => r.idx),
              adjusted_count: summary.adjusted,
              discarded_count: summary.discarded,
              claim:
                auditClaims.length === 1
                  ? auditClaims[0]
                  : auditClaims.slice(0, 3).join("; "),
              ...(discarded.length
                ? relationshipAuditFields(rows[discarded[0]], anchorName)
                : {}),
            },
        { persistImmediately: !onlyIndices?.length }
      );

      if (!onlyIndices?.length && (summary.adjusted || summary.discarded)) {
        setMetamodelSyncNotice({
          adjusted: summary.adjusted,
          discarded: summary.discarded,
        });
      }

      setRelActions((prev) => {
        const next = { ...prev };
        const pv2 = previewRef.current;
        const rows2 = Array.isArray(pv2?.proposed_relationships)
          ? pv2.proposed_relationships
          : [];
        discarded.forEach((idx) => {
          const k = `p${idx}`;
          const eff = effectiveRelReviewState(pv2, rows2[idx], idx, prev);
          if (eff?.state === "approved" || eff?.state === "discarded") return;
          next[k] = { ...(next[k] || {}), state: "discarded", error: "" };
        });
        return next;
      });
    },
    [applyPreviewPatch, editingEntity, preview?.anchor_name]
  );

  const applyEntityTypeChangeNow = useCallback(
    (entry, newType, suggested, focusRelIdx) => {
      const patch =
        !newType || normalizeTypeForCompare(newType) === normalizeTypeForCompare(suggested)
          ? { [entry.key]: null }
          : { [entry.key]: newType };
      const basePreview = previewRef.current || preview;
      if (!basePreview) return;
      const nextPreview = (() => {
        let next = setEntityTypeOverridesOutcome(basePreview, patch);
        const overrides = next?.enrichment_review_outcomes?.entity_type_overrides || {};
        if (focusRelIdx != null) {
          next = applyInstantMetamodelForRelationship(
            next,
            overrides,
            editingEntity,
            focusRelIdx
          );
        }
        if (newType && normalizeTypeForCompare(newType) !== normalizeTypeForCompare(suggested)) {
          next = applyEntityTypeOverrideSideEffects(next, {
            rosterKey: entry.key,
            suggestedType: suggested,
            newType,
          });
        }
        return pruneDeadReviewCards(refreshSolutionStructureCatalogRows(next));
      })();
      metamodelSyncSkipDebounceUntilRef.current = Date.now() + 500;
      previewRef.current = nextPreview;
      applyPreviewPatch(
        () => nextPreview,
        {
          action: "enrichment_roster_entity_type_override",
          roster_key: entry.key,
          type: newType || suggested,
        },
        { persistImmediately: true }
      );
      const overrides = nextPreview?.enrichment_review_outcomes?.entity_type_overrides || {};
      const proposed = Array.isArray(nextPreview.proposed_relationships)
        ? nextPreview.proposed_relationships
        : [];
      let syncIndices = proposedIndicesTouchingRosterKey(
        entry.key,
        proposed,
        nextPreview,
        editingEntity
      );
      if (!syncIndices.length && focusRelIdx != null) {
        syncIndices = [focusRelIdx];
      }
      if (syncIndices.length) {
        void syncRelationshipsForMetamodel(nextPreview, overrides, {
          onlyIndices: syncIndices,
        });
      }
      void autoCompleteResolvedStructureLinksRef.current?.();
    },
    [applyPreviewPatch, syncRelationshipsForMetamodel, editingEntity, preview]
  );

  const handleRosterEntityTypeChange = useCallback(
    async (entry, newTypeRaw) => {
      const newType = String(newTypeRaw || "").trim();
      const suggested = String(entry?.suggestedType || "").trim();
      const focusRelIdx =
        typeof entry?.focusRelIdx === "number" && entry.focusRelIdx >= 0
          ? entry.focusRelIdx
          : null;
      const rosterKey = String(entry?.key || "").trim();

      if (!newType || normalizeTypeForCompare(newType) === normalizeTypeForCompare(suggested)) {
        setTypeChangePending(null);
        applyEntityTypeChangeNow(entry, newType, suggested, focusRelIdx);
        return;
      }

      const basePreview = previewRef.current || preview;
      if (!basePreview) return;

      setTypeChangePending({
        rosterKey,
        pendingType: newType,
        entityName: displayNameForRosterTypeChange(entry, basePreview),
      });

      const patch = { [entry.key]: newType };
      let tempPreview = setEntityTypeOverridesOutcome(basePreview, patch);
      let overrides = tempPreview?.enrichment_review_outcomes?.entity_type_overrides || {};
      if (focusRelIdx != null) {
        tempPreview = applyInstantMetamodelForRelationship(
          tempPreview,
          overrides,
          editingEntity,
          focusRelIdx
        );
      }
      tempPreview = applyEntityTypeOverrideSideEffects(tempPreview, {
        rosterKey: entry.key,
        suggestedType: suggested,
        newType,
      });
      tempPreview = pruneDeadReviewCards(
        refreshSolutionStructureCatalogRows(tempPreview)
      );
      overrides = tempPreview?.enrichment_review_outcomes?.entity_type_overrides || {};

      const proposed = Array.isArray(tempPreview.proposed_relationships)
        ? tempPreview.proposed_relationships
        : [];
      let onlyIndices = proposedIndicesTouchingRosterKey(
        entry.key,
        proposed,
        tempPreview,
        editingEntity
      );
      if (!onlyIndices.length && focusRelIdx != null) {
        onlyIndices = [focusRelIdx];
      }

      try {
        if (!onlyIndices.length) {
          setTypeChangePending(null);
          applyEntityTypeChangeNow(entry, newType, suggested, focusRelIdx);
          return;
        }

        const { discarded, repairs, summary } = await reconcileProposedRelationshipsForMetamodel(
          tempPreview,
          overrides,
          editingEntity,
          relActionsRef.current,
          { onlyIndices }
        );

        if (!summary.discarded && !summary.adjusted) {
          setTypeChangePending(null);
          applyEntityTypeChangeNow(entry, newType, suggested, focusRelIdx);
          return;
        }

        setTypeChangePending(null);
        setTypeChangeConfirmation({
          entry,
          newType,
          suggested,
          focusRelIdx,
          entityName: displayNameForRosterTypeChange(entry, basePreview),
          discardedIndices: discarded,
          summary,
        });
      } catch {
        setTypeChangePending(null);
        applyEntityTypeChangeNow(entry, newType, suggested, focusRelIdx);
      }
    },
    [applyEntityTypeChangeNow, editingEntity, preview]
  );

  const handleConfirmTypeChange = useCallback(() => {
    if (!typeChangeConfirmation) return;
    const {
      entry,
      newType,
      suggested,
      focusRelIdx,
      discardedIndices = [],
    } = typeChangeConfirmation;
    setTypeChangePending(null);
    setTypeChangeConfirmation(null);
    applyEntityTypeChangeNow(entry, newType, suggested, focusRelIdx);

    const toDiscard = discardedIndices.filter(
      (idx) => typeof focusRelIdx !== "number" || idx !== focusRelIdx
    );
    if (toDiscard.length) {
      const pv = previewRef.current;
      if (pv) {
        applyPreviewPatch(
          (p) =>
            applyMetamodelReconcileResultsToPreview(
              p,
              { discarded: toDiscard, repairs: [] },
              relActionsRef.current
            ),
          {
            action: "discard_proposed_relationships_metamodel_invalidation",
            indices: toDiscard,
            discarded_count: toDiscard.length,
          },
          { persistImmediately: true }
        );
        setRelActions((prev) => {
          const next = { ...prev };
          const pv2 = previewRef.current;
          const rows2 = Array.isArray(pv2?.proposed_relationships)
            ? pv2.proposed_relationships
            : [];
          toDiscard.forEach((idx) => {
            const k = `p${idx}`;
            const eff = effectiveRelReviewState(pv2, rows2[idx], idx, prev);
            if (eff?.state === "approved" || eff?.state === "discarded") return;
            next[k] = { ...(next[k] || {}), state: "discarded", error: "" };
          });
          return next;
        });
      }
    }

  }, [applyEntityTypeChangeNow, applyPreviewPatch, typeChangeConfirmation]);

  const handleCancelTypeChange = useCallback(() => {
    setTypeChangePending(null);
    setTypeChangeConfirmation(null);
  }, []);

  const handleRosterApprove = useCallback(
    (entry) => {
      applyPreviewPatch((p) => setEntityRosterApprovalsOutcome(p, { [entry.key]: "approved" }), {
        action: "approve_enrichment_roster_entity",
        roster_key: entry.key,
        entity_name: entry?.label || entry?.name || entry.key,
        entity_type: entry?.suggestedType || entry?.type,
      });
      setRosterRowActions((prev) => ({
        ...prev,
        [entry.key]: { ...(prev[entry.key] || {}), state: "approved", error: "" },
      }));
    },
    [applyPreviewPatch]
  );

  const handleRosterDiscard = useCallback(
    (entry) => {
      if (entry?.category === "anchor") return;
      const indices = proposedIndicesTouchingRosterKey(
        entry.key,
        proposed,
        preview,
        editingEntity
      );
      applyPreviewPatch(
        (p) => {
          let next = setDiscardedRosterKeysOutcome(p, [entry.key]);
          indices.forEach((idx) => {
            next = setProposedRelationshipOutcome(next, `p${idx}`, "discarded");
          });
          return next;
        },
        {
          action: "discard_enrichment_roster_entity",
          roster_key: entry.key,
          proposed_indices: indices,
        }
      );
      if (indices.length) {
        setRelActions((prev) => {
          const next = { ...prev };
          indices.forEach((idx) => {
            next[`p${idx}`] = { ...(next[`p${idx}`] || {}), state: "discarded", error: "" };
          });
          return next;
        });
      }
      setRosterRowActions((prev) => ({
        ...prev,
        [entry.key]: { ...(prev[entry.key] || {}), state: "discarded", error: "" },
      }));
    },
    [applyPreviewPatch, proposed, preview, editingEntity]
  );

  useEffect(() => {
    if (!open || !preview) return;
    const overrides = preview?.enrichment_review_outcomes?.entity_type_overrides || {};
    if (!Object.keys(overrides).length) return;

    const rows = Array.isArray(preview.proposed_relationships)
      ? preview.proposed_relationships
      : [];
    const hasPendingForValidation = rows.some((rel, idx) => {
      if (String(rel?.existing_relationship_id || "").trim()) return false;
      const eff = effectiveRelReviewState(preview, rel, idx, relActionsRef.current);
      if (
        eff?.state === "approved" ||
        eff?.state === "discarded" ||
        eff?.state === "approving" ||
        eff?.state === "discarding"
      ) {
        return false;
      }
      const { canValidate } = effectiveEndpointTypesForValidation(
        rel,
        overrides,
        preview,
        editingEntity
      );
      return canValidate;
    });
    if (!hasPendingForValidation) return;

    let cancelled = false;
    const delayMs = Math.max(
      0,
      metamodelSyncSkipDebounceUntilRef.current - Date.now()
    );
    const handle = window.setTimeout(() => {
      void (async () => {
        if (cancelled) return;
        const pv = previewRef.current;
        const ov = pv?.enrichment_review_outcomes?.entity_type_overrides || {};
        if (!pv || !Object.keys(ov).length) return;
        try {
          await syncRelationshipsForMetamodel(pv, ov, { skipDiscards: true });
        } catch (e) {
          console.error(e);
        }
      })();
    }, delayMs + 280);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [entityTypeOverridesSig, open, editingEntity?.id, syncRelationshipsForMetamodel]);

  useEffect(() => {
    if (!metamodelSyncNotice) return undefined;
    const t = window.setTimeout(() => setMetamodelSyncNotice(null), 12000);
    return () => window.clearTimeout(t);
  }, [metamodelSyncNotice]);

  const resolveAnchorEntityId = useCallback(() => {
    return String(
      editingEntity?.id || preview?.anchor_application_id || ""
    ).trim();
  }, [editingEntity?.id, preview?.anchor_application_id]);

  const handleAcceptDescription = useCallback(async () => {
    const text = (descProposal?.proposed_description || "").trim();
    const anchorId = resolveAnchorEntityId();
    if (!text) {
      setDescError("No proposed description to apply.");
      return;
    }
    if (!deferMapWrites && !anchorId) {
      setDescError(
        "Cannot update the app description because the app id is missing from this upload."
      );
      return;
    }
    setDescError("");
    setDescBusyTracked(true);
    try {
      if (!deferMapWrites) {
        await api.updateEntity(anchorId, { description: text }, deferRag);
        trackDeferredRagEntities(anchorId);
      }
      setDescAccepted(true);
      setDescSkipped(false);
      applyPreviewPatch(
        (p) => setDescriptionAppliedOutcome(p),
        {
          action: deferMapWrites
            ? "apply_description_deferred"
            : "apply_description",
          entity_id: anchorId,
          entity_name: editingEntity?.name || preview?.anchor_name,
        },
        { persistImmediately: true }
      );
      if (typeof onDescriptionApplied === "function") {
        onDescriptionApplied(text);
      }
    } catch (e) {
      console.error(e);
      const msg =
        e?.response?.data?.detail ||
        e?.message ||
        "Could not update the application description.";
      setDescError(typeof msg === "string" ? msg : "Could not update the application description.");
    } finally {
      setDescBusyTracked(false);
    }
  }, [
    applyPreviewPatch,
    deferMapWrites,
    deferRag,
    descProposal,
    editingEntity?.name,
    setDescBusyTracked,
    preview?.anchor_name,
    onDescriptionApplied,
    resolveAnchorEntityId,
    trackDeferredRagEntities,
  ]);

  const descriptionAppliedEarly = isDescriptionReviewResolved(preview, {
    descAccepted,
    descSkipped,
  });

  const reviewQueue = useMemo(
    () =>
      buildReviewQueue({
        preview,
        editingEntity,
        relActions,
        catalogActions,
        matchActions,
        descriptionApplied: descriptionAppliedEarly,
      }),
    [preview, editingEntity, relActions, catalogActions, matchActions, descriptionAppliedEarly]
  );

  const decidedReviewItems = useMemo(
    () =>
      buildDecidedReviewItems({
        preview,
        editingEntity,
        relActions,
        catalogActions,
        descriptionApplied: descriptionAppliedEarly,
      }),
    [preview, editingEntity, relActions, catalogActions, descriptionAppliedEarly]
  );

  const queueStats = useMemo(
    () => countQueueStats(reviewQueue, relActions, catalogActions, preview),
    [reviewQueue, relActions, catalogActions, preview]
  );

  const anchorSummary = useMemo(
    () => anchorSummaryLine(preview, editingEntity),
    [preview, editingEntity]
  );

  useEffect(() => {
    if (!inline || theme !== "light" || typeof onQueueStatsChange !== "function") {
      return;
    }
    onQueueStatsChange(queueStats);
  }, [inline, theme, queueStats, onQueueStatsChange]);

  const pendingCatalogSaveCount = useMemo(() => {
    const jid = String(reviewJobId || "").trim();
    if (jid) return getReviewBackgroundInFlight(jid);
    return catalogSaveInFlightRef.current;
  }, [saveBusyTick, bgSaveTick, reviewJobId]);
  const catalogSavesBusy = useMemo(
    () => pendingCatalogSaveCount > 0 || descBusy,
    [pendingCatalogSaveCount, descBusy]
  );
  const reviewGlobalLocked = useMemo(() => closingReview, [closingReview]);
  const reviewSaveLocked = useMemo(
    () => catalogSavesBusy || reviewGlobalLocked,
    [catalogSavesBusy, reviewGlobalLocked]
  );
  const saveStatusMessage = useMemo(
    () =>
      enrichmentReviewSaveStatusMessage(pendingCatalogSaveCount, {
        descriptionInFlight: descBusy,
      }),
    [pendingCatalogSaveCount, descBusy]
  );

  useEffect(() => {
    if (typeof onSaveBusyChange !== "function") return;
    const pendingCount = pendingCatalogSaveCount + (descBusy ? 1 : 0);
    const prev = lastSaveBusyNotifyRef.current;
    if (
      prev &&
      prev.locked === reviewSaveLocked &&
      prev.pendingCount === pendingCount &&
      prev.message === saveStatusMessage &&
      prev.closing === closingReview
    ) {
      return;
    }
    lastSaveBusyNotifyRef.current = {
      locked: reviewSaveLocked,
      pendingCount,
      message: saveStatusMessage,
      closing: closingReview,
    };
    onSaveBusyChange({
      locked: reviewSaveLocked,
      pendingCount,
      message: saveStatusMessage,
      closing: closingReview,
      requestClose: requestCloseStable,
    });
  }, [
    onSaveBusyChange,
    reviewSaveLocked,
    descBusy,
    closingReview,
    saveStatusMessage,
    pendingCatalogSaveCount,
    requestCloseStable,
  ]);

  const resolveCatalogRowForApprove = useCallback((item) => {
    const pv = previewRef.current || preview;
    const rows = Array.isArray(pv?.proposed_catalog_entities)
      ? pv.proposed_catalog_entities
      : [];
    return applyCatalogEndpointCandidates(rows[item.idx] || item.row);
  }, [preview]);

  const runReviewApprove = useCallback(
    async (item, opts = {}) => {
      if (!item) return;
      if (item.type === "relationship") {
        await handleApproveRel(item.row, item.idx);
      } else if (item.type === "catalog") {
        const row = resolveCatalogRowForApprove(item);
        await handleAddInterfaceStub(row, item.idx, opts);
      } else if (item.type === "description") {
        await handleAcceptDescription();
      }
    },
    [handleAcceptDescription, handleAddInterfaceStub, handleApproveRel, resolveCatalogRowForApprove]
  );

  const handleApproveItem = useCallback(
    (item) => {
      if (!item || reviewGlobalLocked) return;
      void runReviewApprove(item).catch((e) => {
        const errText = extractActionErrorMessage(e, "Could not approve this item");
        setGlobalActionError(errText);
        console.error("Review approve failed:", e);
      });
    },
    [reviewGlobalLocked, runReviewApprove]
  );

  const handleCreateNewItem = useCallback(
    (item) => {
      if (!item || reviewGlobalLocked) return;
      void runReviewApprove(item, { forceCreateNew: true }).catch((e) => {
        const errText = extractActionErrorMessage(e, "Could not create this item");
        setGlobalActionError(errText);
        console.error("Review create-new failed:", e);
      });
    },
    [reviewGlobalLocked, runReviewApprove]
  );

  const handleDiscardItem = useCallback(
    (item) => {
      if (!item || reviewGlobalLocked) return;
      if (item.type === "relationship") {
        handleDiscardRel(item.row, item.idx);
      } else if (item.type === "catalog") {
        handleDiscardInterfaceSuggestion(item.row, item.idx);
      } else if (item.type === "description") {
        setDescSkipped(true);
        setDescAccepted(false);
        applyPreviewPatch(
          (p) => setDescriptionDiscardedOutcome(p),
          { action: "discard_description" },
          { persistImmediately: true }
        );
      }
    },
    [applyPreviewPatch, handleDiscardRel, handleDiscardInterfaceSuggestion, reviewGlobalLocked]
  );

  const handleReviseDecidedItem = useCallback(
    (item) => {
      if (!item || reviewSaveLocked) return;
      if (item.type === "relationship") {
        const key = `p${item.idx}`;
        setRelActions((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        applyPreviewPatch(
          (p) => clearProposedRelationshipOutcome(p, key),
          { action: "revise_proposed_relationship", proposed_key: key },
          { persistImmediately: true }
        );
      } else if (item.type === "catalog") {
        const ck = catalogStubKey(item.row, item.idx);
        setCatalogActions((prev) => {
          const next = { ...prev };
          delete next[ck];
          return next;
        });
        applyPreviewPatch(
          (p) => clearCatalogEntityOutcome(p, ck),
          { action: "revise_catalog_entity", catalog_key: ck },
          { persistImmediately: true }
        );
      } else if (item.type === "description") {
        setDescAccepted(false);
        setDescSkipped(false);
        applyPreviewPatch(
          (p) => clearDescriptionAppliedOutcome(p),
          { action: "revise_description" },
          { persistImmediately: true }
        );
      }
    },
    [applyPreviewPatch, reviewSaveLocked]
  );

  const handleRenameReviewItem = useCallback(
    (item, entityKey, name) => {
      applyPreviewPatch(
        (p) => renameReviewItemEntity(p, item, entityKey, name),
        {
          action: "rename_review_entity",
          item_id: item?.id,
          entity_key: entityKey,
          name: String(name || "").trim(),
        },
        { persistImmediately: true }
      );
    },
    [applyPreviewPatch]
  );

  const handleMarkReviewComplete = useCallback(() => {
    if (closingReview) return;
    const stewardPending = reviewQueue.length;
    if (stewardPending > 0) {
      const ok = window.confirm(
        `Mark this review complete?\n\n${stewardPending} suggestion${
          stewardPending === 1 ? " has" : "s have"
        } not been approved or discarded. The job will leave "Needs review" in the inbox. You can reopen it later to continue.`
      );
      if (!ok) return;
    }
    applyPreviewPatch(
      (p) => setReviewCompleteOutcome(p, true),
      {
        action: "mark_review_complete",
        pending_skipped: stewardPending,
      },
      { persistImmediately: true }
    );
    emitGuideEvent("review-complete", { jobId: String(reviewJobId || "") });
  }, [applyPreviewPatch, closingReview, reviewJobId, reviewQueue.length]);

  const handleResumeReview = useCallback(() => {
    applyPreviewPatch(
      (p) => setReviewCompleteOutcome(p, false),
      { action: "resume_enrichment_review" },
      { persistImmediately: true }
    );
  }, [applyPreviewPatch]);

  const handleBulkAcceptCatalogMatches = useCallback(async () => {
    if (reviewSaveLocked || closingReview || bulkAcceptProgress) return;
    const items = filterAcceptableAiRecommendationItems(reviewQueue);
    if (!items.length) return;
    const n = items.length;
    const ok = window.confirm(
      `Accept ${n} AI recommendation${n === 1 ? "" : "s"}?\n\nEach uses the same action as Add to map / Use on map on each card. Name-matching decisions are left for you to review.`
    );
    if (!ok) return;
    setGlobalActionError("");
    setBulkAcceptProgress({ current: 0, total: n });
    try {
      for (let i = 0; i < items.length; i += 1) {
        setBulkAcceptProgress({ current: i + 1, total: n });
        await runReviewApprove(items[i]);
      }
    } catch (e) {
      const errText = extractActionErrorMessage(
        e,
        "Could not accept all AI recommendations"
      );
      setGlobalActionError(errText);
      console.error("Bulk accept AI recommendations failed:", e);
    } finally {
      setBulkAcceptProgress(null);
    }
  }, [
    reviewSaveLocked,
    closingReview,
    bulkAcceptProgress,
    reviewQueue,
    runReviewApprove,
  ]);

  const bulkAcceptBusy = bulkAcceptProgress !== null;
  const bulkAcceptStatusMessage = bulkAcceptProgress
    ? `Accepting AI recommendations (${bulkAcceptProgress.current} of ${bulkAcceptProgress.total})…`
    : "";
  const panelSaveStatusMessage = bulkAcceptStatusMessage || saveStatusMessage;

  /**
   * Auto-complete when every steward card is decided and every map save finished
   * (including background saves after the user left and came back).
   */
  useEffect(() => {
    if (!open || !preview) return;
    if (
      !shouldAutoCompleteEnrichmentReview(preview, reviewQueue.length, {
        backgroundBusy: pendingCatalogSaveCount > 0,
      })
    ) {
      return;
    }
    applyPreviewPatchRef.current?.(
      (p) => setReviewCompleteOutcome(p, true),
      { action: "mark_review_complete" },
      { persistImmediately: true }
    );
    emitGuideEvent("review-complete", {
      jobId: String(reviewJobId || preview?.job_id || ""),
    });
  }, [
    open,
    preview,
    preview?.job_id,
    preview?.enrichment_review_outcomes?.review_complete,
    preview?.enrichment_review_outcomes?.catalog_entities,
    preview?.enrichment_review_outcomes?.proposed_relationships,
    reviewQueue.length,
    pendingCatalogSaveCount,
    reviewJobId,
  ]);

  useEffect(() => {
    if (!open || !preview) return;
    const outcomes = preview?.enrichment_review_outcomes || {};
    setDescAccepted(Boolean(outcomes.description_applied));
    setDescSkipped(Boolean(outcomes.description_discarded));
    setDescError("");
  }, [
    open,
    preview?.job_id,
    preview?.anchor_application_id,
    preview?.enrichment_review_outcomes?.description_applied,
    preview?.enrichment_review_outcomes?.description_discarded,
  ]);

  /** Jobs reviewed before description_applied was persisted: infer from live entity text. */
  useEffect(() => {
    if (!open || !preview) return undefined;
    const proposed = (descProposal?.proposed_description || "").trim();
    if (!proposed) return undefined;
    if (isDescriptionReviewResolved(preview)) return undefined;
    const anchorId = resolveAnchorEntityId();
    if (!anchorId) return undefined;
    let cancelled = false;
    void (async () => {
      try {
        const ent = await api.getEntity(anchorId, { includeUnapproved: true });
        if (cancelled) return;
        const current = normalizeDescriptionText(ent?.description);
        if (!current || current !== normalizeDescriptionText(proposed)) return;
        setDescAccepted(true);
        setDescSkipped(false);
        applyPreviewPatchRef.current?.(
          (p) => setDescriptionAppliedOutcome(p),
          { action: "infer_description_already_applied", entity_id: anchorId }
        );
      } catch {
        /* ignore — user can accept manually */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    preview?.job_id,
    preview?.enrichment_review_outcomes?.description_applied,
    preview?.enrichment_review_outcomes?.description_discarded,
    descProposal?.proposed_description,
    resolveAnchorEntityId,
  ]);

  /** Legacy jobs may still have an unapproved `anchor` roster row — auto-approve (anchor is always application). */
  useEffect(() => {
    if (!open || !preview) return;
    const approvals = preview?.enrichment_review_outcomes?.entity_roster_approvals || {};
    if (approvals.anchor === "approved") return;
    applyPreviewPatchRef.current?.(
      (p) => setEntityRosterApprovalsOutcome(p, { anchor: "approved" }),
      { action: "auto_approve_anchor_roster", roster_key: "anchor" }
    );
    setRosterRowActions((prev) => ({
      ...prev,
      anchor: { ...(prev.anchor || {}), state: "approved", error: "" },
    }));
  }, [
    open,
    preview?.job_id,
    preview?.anchor_application_id,
    preview?.enrichment_review_outcomes?.entity_roster_approvals?.anchor,
  ]);

  if (!open || !preview) return null;
  const isLight = theme === "light";
  /** Light chrome for queue cards / CSS bridge (inbox + refine modal). */
  const isInlineLight = isLight;
  const descriptionApplied = descriptionAppliedEarly;
  const renderRosterTypeSelect = (
    rosterKey,
    suggestedType,
    disabled,
    light = false,
    focusRelIdx = null,
    surfaceName = ""
  ) => {
    if (!rosterKey) return null;
    const sk = String(suggestedType || "application").trim();
    const overridesMap = preview?.enrichment_review_outcomes?.entity_type_overrides || {};
    const effectiveType = overridesMap[rosterKey] ?? sk;
    const pendingForThis =
      typeChangePending?.rosterKey === rosterKey
        ? typeChangePending
        : typeChangeConfirmation?.entry?.key === rosterKey
          ? {
              rosterKey,
              pendingType: typeChangeConfirmation.newType,
            }
          : null;
    const displayType = pendingForThis?.pendingType || effectiveType;
    const checkingImpact = Boolean(
      typeChangePending && typeChangePending.rosterKey === rosterKey
    );
    const typeOptionKnown =
      entityTypeOptions.length === 0 ||
      entityTypeOptions.some((o) => o.value === displayType);
    const selClass = light
      ? "max-w-[260px] rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 disabled:opacity-50"
      : "max-w-[260px] rounded border border-slate-600 bg-slate-900 px-2 py-1 text-xs text-slate-100 disabled:opacity-50";
    return (
      <span className="inline-flex items-center gap-1.5">
        <select
          value={displayType}
          onChange={(e) =>
            handleRosterEntityTypeChange(
              {
                key: rosterKey,
                suggestedType: sk,
                surfaceName: String(surfaceName || "").trim(),
                ...(typeof focusRelIdx === "number" && focusRelIdx >= 0
                  ? { focusRelIdx }
                  : {}),
              },
              e.target.value
            )
          }
          disabled={disabled || Boolean(typeChangePending) || Boolean(typeChangeConfirmation)}
          aria-busy={checkingImpact}
          className={selClass}
        >
          {entityTypeOptions.length === 0 ? (
            <option value={displayType}>{displayType}</option>
          ) : (
            <>
              {!typeOptionKnown && displayType ? (
                <option value={displayType}>{displayType} (from job)</option>
              ) : null}
              {entityTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label || opt.value}
                </option>
              ))}
            </>
          )}
        </select>
        {checkingImpact ? (
          <span
            className={
              light
                ? "inline-flex items-center gap-1 text-[11px] text-slate-500"
                : "inline-flex items-center gap-1 text-[11px] text-slate-400"
            }
            role="status"
          >
            <span
              className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
              aria-hidden
            />
            Updating…
          </span>
        ) : null}
      </span>
    );
  };

  const backdropClass = inline
    ? "w-full"
    : isLight
      ? "fixed inset-0 z-[70] overflow-y-auto overscroll-contain bg-slate-900/35 backdrop-blur-sm"
      : "fixed inset-0 z-[70] overflow-y-auto overscroll-contain bg-black/70 backdrop-blur-sm";
  const backdropInnerClass = inline
    ? ""
    : "flex min-h-full items-start sm:items-center justify-center p-4";
  const panelClass = inline
    ? "w-full"
    : isLight
      ? "bg-white border border-slate-200 rounded-xl shadow-2xl w-full max-w-4xl flex flex-col my-4 sm:my-8"
      : "bg-slate-900 border border-slate-600/80 rounded-xl shadow-2xl w-full max-w-4xl flex flex-col my-4 sm:my-8";
  const contentClass = inline
    ? "space-y-3"
    : "px-6 py-5 space-y-6";
  const headerClass = inline
    ? "pb-4 border-b border-slate-200 flex items-start justify-between gap-4"
    : isLight
      ? "px-6 py-4 border-b border-slate-200 bg-white flex items-start justify-between gap-4"
      : "px-6 py-4 border-b border-slate-700/90 bg-gradient-to-r from-slate-900 to-slate-800 flex items-start justify-between gap-4";

  const renderRelationshipKindGroups = (grouped, options = {}) => {
    const showKindHeaders = options.showKindHeaders !== false;
    const unresolvedIdxSet = options.unresolvedIdxSet;
    if (Object.keys(grouped).length === 0) {
      return (
        <p className="text-sm text-slate-500">No relationship suggestions.</p>
      );
    }
    return (
      <div className="space-y-4">
        {Object.entries(grouped).map(([kind, items]) => (
          <div
            key={kind}
            className="rounded-xl border border-slate-700 bg-slate-800/30 overflow-hidden"
          >
            {showKindHeaders ? (
              <div className="px-4 py-2 bg-slate-800/80 border-b border-slate-700 text-xs font-medium text-slate-300 uppercase tracking-wide">
                {kind.replace(/_/g, " ")}
              </div>
            ) : null}
            <ul className="divide-y divide-slate-700/80">
              {items.map(({ rel, idx }) => {
                const key = relationKey(rel, idx);
                const action = effectiveRelReviewState(preview, rel, idx, relActions);
                const cact = catalogActions[key] || {};
                const relEff = augmentRelMissingEndpoint(rel);
                const peer = peerLabel(rel);
                const resolvable = isResolvable(relEff);
                const isExisting = Boolean(relEff?.existing_relationship_id);
                const isObsolete = isObsoleteRel(relEff);
                const canAutoCreateOnApprove =
                  !resolvable &&
                  !isObsolete &&
                  String(relEff?.missing_surface_name || "").trim() &&
                  String(relEff?.missing_entity_type || "").trim();
                const approveBlockedNoAutoCreate =
                  !isExisting &&
                  !isObsolete &&
                  !resolvable &&
                  !canAutoCreateOnApprove &&
                  action.state !== "approved" &&
                  action.state !== "discarded";
                const highlightUnresolved = unresolvedIdxSet && unresolvedIdxSet.has(idx);
                return (
                  <li
                    key={key}
                    id={`enrich-rel-row-${idx}`}
                    className={`px-4 py-3 hover:bg-slate-800/50 scroll-mt-28 ${
                      highlightUnresolved
                        ? "ring-2 ring-inset ring-amber-500/70 bg-amber-950/15"
                        : ""
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-slate-100">
                          {relationshipSentence(
                            editingEntity?.name ||
                              `This ${(
                                preview?.anchor_entity_type ||
                                editingEntity?.type ||
                                "application"
                              )
                                .toString()
                                .replace(/_/g, "-")
                                .replace(/-/g, " ")}`,
                            rel,
                            preview?.anchor_entity_type ||
                              editingEntity?.type ||
                              "application"
                          )}
                        </p>
                        {isObsolete ? (
                          <p className="text-xs text-amber-300/90 mt-1">
                            On your map, but not supported by current knowledge —
                            confirm to remove, or skip to keep.
                          </p>
                        ) : null}
                        {isExisting ? (
                          <p className="text-xs text-emerald-300/90 mt-1">
                            This connection is already on your map and does not need
                            approval.
                          </p>
                        ) : null}
                        {!isExisting && action.state === "approved" ? (
                          <p className="text-xs text-emerald-300/90 mt-1 font-medium">
                            {isObsolete
                              ? "Removed from your map."
                              : "Added to your map."}
                          </p>
                        ) : null}
                        {!isExisting && action.state === "discarded" ? (
                          <p className="text-xs text-rose-300/90 mt-1 font-medium">
                            {isObsolete
                              ? "Kept on your map."
                              : "Skipped — not added to your map."}
                          </p>
                        ) : null}
                        {!isExisting &&
                        action.state !== "approved" &&
                        action.state !== "discarded" &&
                        !resolvable ? (
                          <p className="text-xs text-amber-400/90 mt-1">
                            {relEff?.missing_end === "source"
                              ? `Missing source (${relEff.missing_entity_type || "?"}): ${relEff.missing_surface_name || "—"}`
                              : relEff?.missing_end === "target"
                                ? `Missing target (${relEff.missing_entity_type || "?"}): ${relEff.missing_surface_name || "—"}`
                                : "Not on your map yet — match the name first."}
                          </p>
                        ) : null}
                        {!isExisting &&
                        relEff?.missing_end &&
                        String(relEff?.missing_surface_name || "").trim() &&
                        String(relEff?.missing_entity_type || "").trim() &&
                        !isEnrichmentAnchorEntity(preview, editingEntity, {
                          surfaceName: relEff.missing_surface_name,
                          entityType: relEff.missing_entity_type,
                        }) ? (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="text-xs text-slate-400">
                              Type ({relEff.missing_end}) for{" "}
                              <span className="font-medium text-slate-200">
                                {String(relEff.missing_surface_name || "").trim()}
                              </span>
                              :
                            </span>
                            {renderRosterTypeSelect(
                              rosterTypeOverrideKeyForGap(
                                preview,
                                relEff.missing_surface_name,
                                relEff.missing_entity_type,
                                editingEntity
                              ),
                              String(relEff.missing_entity_type || "").trim(),
                              isExisting ||
                                action.state === "approved" ||
                                action.state === "discarded" ||
                                action.state === "approving" ||
                                action.state === "discarding",
                              false,
                              idx,
                              String(relEff.missing_surface_name || "").trim()
                            )}
                          </div>
                        ) : null}
                        {!isExisting && approveBlockedNoAutoCreate ? (
                          <p className="text-xs text-rose-300/90 mt-2 border-l-2 border-rose-600/70 pl-2">
                            This row still cannot create or map an entity automatically (no usable
                            name and type for the gap). Use candidate matches if shown, or{" "}
                            <span className="font-medium text-slate-200">Skip</span>.
                          </p>
                        ) : null}
                        {!resolvable &&
                        Array.isArray(rel?.candidate_matches) &&
                        rel.candidate_matches.length > 0 ? (
                          <div className="mt-2">
                            <p className="text-xs text-slate-400">Possible existing matches:</p>
                            <div className="flex flex-wrap gap-2 mt-1">
                              {rel.candidate_matches.slice(0, 3).map((cand) => (
                                <button
                                  key={`rel-cand-${key}-${cand.id}`}
                                  type="button"
                                  onClick={() =>
                                    void handleUseExistingCandidate(
                                      cand,
                                      rel?.missing_surface_name,
                                      rel?.missing_entity_type
                                    )
                                  }
                                  className="px-2 py-1 rounded bg-indigo-700/70 hover:bg-indigo-600 text-xs text-white"
                                >
                                  Use existing: {cand.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        <EnrichmentContextNotes
                          row={rel}
                          anchorName={
                            editingEntity?.name || preview?.anchor_name || ""
                          }
                          isInlineLight={isInlineLight}
                          variant="card"
                          previewCatalogOnCreate={Boolean(
                            augmentRelMissingEndpoint(rel)?.missing_end &&
                              String(rel?.missing_surface_name || "").trim() &&
                              String(rel?.missing_entity_type || "").trim()
                          )}
                          createEntityType={rel?.missing_entity_type}
                          createEntityName={rel?.missing_surface_name}
                        />
                      </div>
                      <div className="flex flex-wrap items-center gap-2 shrink-0 justify-end">
                        <button
                          type="button"
                          onClick={() => void handleApproveRel(rel, idx)}
                          disabled={
                            isExisting ||
                            action.state === "approved" ||
                            action.state === "discarded" ||
                            action.state === "approving" ||
                            action.state === "discarding" ||
                            (!resolvable && !canAutoCreateOnApprove)
                          }
                          title={
                            isObsolete
                              ? "Remove this connection from your map"
                              : !resolvable && !canAutoCreateOnApprove && !isExisting
                                ? "Match the name on your map first, or we need a name and type to add as new"
                                : undefined
                          }
                          className={`px-3 py-1.5 rounded-lg text-white text-xs font-medium disabled:opacity-40 ${
                            isObsolete
                              ? "bg-amber-800/90 hover:bg-amber-700"
                              : "bg-emerald-800/90 hover:bg-emerald-700"
                          }`}
                        >
                          {isExisting
                            ? "Already exists"
                            : action.state === "approving"
                              ? "Saving…"
                              : action.state === "approved"
                                ? isObsolete
                                  ? "Removed"
                                  : "Added"
                                : isObsolete
                                  ? "Remove from map"
                                  : !resolvable && canAutoCreateOnApprove
                                    ? "Add as new + approve"
                                    : "Add to map"}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDiscardRel(rel, idx)}
                          disabled={
                            isExisting ||
                            action.state === "discarded" ||
                            action.state === "approving" ||
                            action.state === "discarding"
                          }
                          className={`px-3 py-1.5 rounded-lg text-xs ${
                            action.state === "discarded"
                              ? "bg-red-700 text-white"
                              : "bg-slate-700 hover:bg-red-900/60 text-slate-100"
                          }`}
                        >
                          {action.state === "discarded"
                            ? isObsolete
                              ? "Kept"
                              : "Skipped"
                            : isObsolete
                              ? "Keep"
                              : "Skip"}
                        </button>
                      </div>
                    </div>
                    {action.state === "discarded" ? (
                      <p className="text-xs text-rose-200/80 mt-2">
                        Kept for traceability. Related suggestions with the same missing entity were
                        cleared too.
                      </p>
                    ) : null}
                    {cact.error ? (
                      <p className="text-xs text-red-400 mt-2">{cact.error}</p>
                    ) : null}
                    {action.error ? (
                      <p className="text-xs text-red-400 mt-2">{action.error}</p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={backdropClass}>
      <div
        className={inline ? undefined : backdropInnerClass}
        onClick={(e) => {
          if (!inline && e.target === e.currentTarget) requestClose();
        }}
      >
        <div
          className={`${panelClass} ${isLight ? "v2-review-light" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          {!inline ? (
            <div className={headerClass}>
              <div>
                <h2
                  className={
                    isLight
                      ? "text-lg font-semibold text-slate-900 tracking-tight"
                      : "text-lg font-semibold text-white tracking-tight"
                  }
                >
                  Review
                </h2>
                <p
                  className={
                    isLight
                      ? "text-sm text-slate-600 mt-1"
                      : "text-sm text-slate-400 mt-1"
                  }
                >
                  {anchorSummary}
                </p>
              </div>
              <button
                type="button"
                onClick={() => requestClose()}
                className={
                  isLight
                    ? "shrink-0 px-3 py-1.5 rounded-lg border border-slate-300 bg-white text-slate-700 text-sm hover:bg-slate-50"
                    : "shrink-0 px-3 py-1.5 rounded-lg bg-slate-700 text-slate-100 text-sm hover:bg-slate-600"
                }
                title={
                  reviewSaveLocked
                    ? "Map updates keep running in the background after you leave"
                    : undefined
                }
              >
                {reviewSaveLocked ? "Leave (saves continue)" : "Close"}
              </button>
            </div>
          ) : null}

          <div className={contentClass}>
            {globalActionError ? (
              <div
                className={
                  isInlineLight
                    ? "rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800"
                    : "rounded-lg border border-rose-500/40 bg-rose-950/40 px-4 py-3 text-sm text-rose-100"
                }
                role="alert"
              >
                <p className="font-medium">Action failed</p>
                <p className="mt-1">{globalActionError}</p>
              </div>
            ) : null}
            {!isLight ? (
              <p className="text-sm text-slate-400">
                {anchorSummary}
                {queueStats.reviewed > 0
                  ? ` · ${queueStats.reviewed} of ${queueStats.total} done`
                  : queueStats.total > 0
                    ? ` · ${queueStats.pending} to review`
                    : ""}
              </p>
            ) : null}

            <EnrichmentReviewQueuePanel
              isInlineLight={isInlineLight}
              anchorSummary={anchorSummary}
              reviewEntity={editingEntity}
              queue={reviewQueue}
              decidedItems={decidedReviewItems}
              stats={queueStats}
              setupNeeded={unresolvedPotentialMatches.length > 0}
              setupComplete={unresolvedPotentialMatches.length === 0}
              potentialMatches={potentialMatches}
              matchActions={matchActions}
              preview={preview}
              relActions={relActions}
              catalogActions={catalogActions}
              descProposal={descProposal}
              descriptionApplied={descriptionApplied}
              descBusy={descBusy}
              descError={descError}
              onConfirmMatch={(row, cand) => void handleConfirmPotentialMatch(row, cand)}
              onKeepNew={handleKeepAsNew}
              onDiscardMatch={handleDiscardPotentialMatch}
              onApproveItem={handleApproveItem}
              onCreateNewItem={handleCreateNewItem}
              onDiscardItem={handleDiscardItem}
              onUseCandidate={(cand, name, type) =>
                void handleUseExistingCandidate(cand, name, type)
              }
              onOpenEntityPicker={handleOpenEntityPicker}
              onAcceptDescription={() => void handleAcceptDescription()}
              onReviseDecided={handleReviseDecidedItem}
              onRenameItem={handleRenameReviewItem}
              renderRosterTypeSelect={renderRosterTypeSelect}
              metamodelSyncNotice={metamodelSyncNotice}
              onDismissMetamodelSyncNotice={() => setMetamodelSyncNotice(null)}
              reviewSaveLocked={reviewSaveLocked || bulkAcceptBusy}
              reviewGlobalLocked={reviewGlobalLocked}
              catalogSavesBusy={catalogSavesBusy}
              saveStatusMessage={panelSaveStatusMessage}
              closingReview={closingReview}
              reviewComplete={Boolean(preview?.enrichment_review_outcomes?.review_complete)}
              onMarkReviewComplete={handleMarkReviewComplete}
              onResumeReview={handleResumeReview}
              onBulkAcceptCatalogMatches={() => void handleBulkAcceptCatalogMatches()}
              bulkBusy={bulkAcceptBusy}
            />
            <EntityTypeChangeConfirmDialog
              confirmation={typeChangeConfirmation}
              isInlineLight={isInlineLight}
              onConfirm={handleConfirmTypeChange}
              onCancel={handleCancelTypeChange}
            />
            <EnrichmentEntityPickerModal
              isOpen={Boolean(entityPickerState)}
              isInlineLight={isInlineLight}
              title={entityPickerState?.title}
              entityType={entityPickerState?.entityType}
              onClose={handleCloseEntityPicker}
              onSelect={(entity) => void handleSelectEntityFromPicker(entity)}
              anchorName={editingEntity?.name || preview?.anchor_name || ""}
              relationshipHint={entityPickerState?.relationshipHint || ""}
              surfaceName={entityPickerState?.surfaceName || ""}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnrichReviewDialog;
